// https://vuex.vuejs.org/en/actions.html
import {
  CRESCIMENTO_TERMINACAO,
  VACA,
  LACTANTE,
  GESTANTE_LACTANTE,
  NAO_GESTANTE,
  ID_LEITE_VACA,
  ID_UREIA,
} from "@/utils/types";
import { simplex } from "@/utils/simplex";
import { parseString, parseMoney, replaceSpecialChars } from "@/utils/vuex";
import { normaliza, chutaKP, createMD5 } from "@/utils/utils";
import { SUBPRODUTOS_VOLUMOSOS } from "../../utils/types";
import Vue from "vue";
import { calculaPesoCV } from "../../utils/utils";
import { forEach } from "csv-string";
import relatorioFinanceiroModelo from "@/models/relatorioFinanceiro";
import _ from "lodash";
import Animal from "@/models/Animal";

export const deepClone = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  // Para objetos Vue/Vuex reativos, às vezes precisamos de uma abordagem diferente
  try {
    // Tenta clonar via JSON (funciona para a maioria dos dados de animais)
    const cloned = JSON.parse(JSON.stringify(obj));
    return cloned;
  } catch (error) {
    console.warn('Falha ao clonar via JSON, usando fallback:', error);
    // Fallback: cópia superficial
    return Array.isArray(obj) ? [...obj] : { ...obj };
  }
};

// Ou use uma das abordagens abaixo, dependendo da sua necessidade:
const cloneMethods = {
  // Método seguro para objetos simples (não funciona com funções, undefined, etc.)
  jsonClone: (obj) => JSON.parse(JSON.stringify(obj)),

  // Método usando structuredClone (nativo do JavaScript moderno)
  structuredClone: (obj) => {
    if (typeof structuredClone === 'function') {
      return structuredClone(obj);
    }
    // Fallback para navegadores antigos
    return JSON.parse(JSON.stringify(obj));
  }
};

export const EPSILON = 1e-6;

export const toNumber = (value) => {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
};

export const toScenarioNumber = (value) => {
  if (typeof value === "number") return isNaN(value) ? 0 : value;
  if (value === null || value === undefined) return 0;

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return 0;

    if (normalized.includes(",") && normalized.includes(".")) {
      const parsed = Number(normalized.replace(/\./g, "").replace(",", "."));
      return isNaN(parsed) ? 0 : parsed;
    }

    if (normalized.includes(",")) {
      const parsed = Number(normalized.replace(",", "."));
      return isNaN(parsed) ? 0 : parsed;
    }

    const parsed = Number(normalized);
    if (!isNaN(parsed)) return parsed;

    const fallback = parseFloat(normalized);
    return isNaN(fallback) ? 0 : fallback;
  }

  return toNumber(value);
};

export const getNutriente = (nutrientes, ...keys) => {
  for (const key of keys) {
    const value = toNumber(_.get(nutrientes, key));
    if (value !== 0) return value;
  }
  return 0;
};

export const round = (value, decimals = 6) => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

export const normalizeScenarioDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

export const daysBetweenDates = (fromDate, toDate) => {
  if (!fromDate || !toDate) return 0;
  const diff = toDate.getTime() - fromDate.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export const compareFloatsEquals = (a, b, epsilon = EPSILON) => {
    return Math.abs(a - b) < epsilon;
};

export const resolveProteinas = ({ alimento, kp }) => {
  const nutrientes = alimento.nutrientes || {};

  let PBA = getNutriente(nutrientes, "PBA", "PB_A", "PB A");
  let PBB = getNutriente(nutrientes, "PBB", "PB_B", "PB B");
  let PBKD = getNutriente(nutrientes, "PBKD", "PB_kd", "PB kd");
  let PB = getNutriente(nutrientes, "PB");

  let PDRPB = getNutriente(nutrientes, "PDRPB", "PDR_PB", "PDR PB");
  let PNDRPB = getNutriente(nutrientes, "PNDRPB", "PNDR_PB", "PNDR PB");

  let PDR = getNutriente(nutrientes, "PDR");
  let PNDR = getNutriente(nutrientes, "PNDR");
  let PM = getNutriente(nutrientes, "PM");

  if (alimento.id === ID_UREIA) {
    PNDR = 0;
    PDR = PB;
  }

  else if (alimento.id === ID_LEITE_VACA) {
    PNDR = PB;
    PDR = 0;
  }

  else if (
    !compareFloatsEquals(PBA, 0) &&
    !compareFloatsEquals(PBB, 0) &&
    !compareFloatsEquals(PBKD, 0) &&
    !compareFloatsEquals(PB, 0)
  ) {
    PDRPB = PBA + (PBB * PBKD) / (PBKD + kp);
    PDR = PDRPB * (PB / 100);
    PNDR = PB - PDR;
  } else if (!compareFloatsEquals(PDR + PNDR, PB)) {
    if (compareFloatsEquals(PB, 0)) {
      if (PDR + PNDR > 0) {
        PB = PDR + PNDR;
      } else {
        PDR = PNDR = 0;
      }
    }
    
    if (
      !compareFloatsEquals(PB, 0) &&
      compareFloatsEquals(PDR, 0) &&
      compareFloatsEquals(PNDR, 0) &&
      !compareFloatsEquals(PDRPB + PNDRPB, 0)
    ) {
      if (!compareFloatsEquals(PDRPB, 0)) PDR = (PDRPB * PB) / 100;
      if (!compareFloatsEquals(PNDRPB, 0)) PNDR = (PNDRPB * PB) / 100;
    }

    let soma = PDR + PNDR;
    if (
      compareFloatsEquals(PDR, 0) &&
      !compareFloatsEquals(PNDR, 0)
    ) {
      PDR = PB - PNDR;
    }
    
    if (
      !compareFloatsEquals(PDR, 0) &&
      compareFloatsEquals(PNDR, 0)
    ) {
      PNDR = PB - PDR;
    }

    if (
      !compareFloatsEquals(PB, 0) &&
      compareFloatsEquals(PDR, 0) &&
      compareFloatsEquals(PNDR, 0)
    ) {
      PDR = PB;
      PNDR = 0;
    }

    soma = PDR + PNDR;

    if (!compareFloatsEquals(soma, PB)) {
      PDR = PB * (PDR / soma);
      PNDR = PB * (PNDR / soma);
    }
  }

  PM = (PDR * 0.64 + PNDR * 0.8) * 10;

  return {
    PDR: round(PDR),
    PNDR: round(PNDR),
    PB: round(PB),
    PM: round(PM),
  };
};


export const relatorioFinanceiro = (
  { commit, state },
  { animal, custo, gmd = null, activeCommit = false, dataVendaEstimada = null }
) => {
  if (state && activeCommit) normaliza(state);

  const animalCopy = animal;
  animalCopy.useFormat = false;

  let relatorioFinanceiro = {};

  if (
    parseFloat(animalCopy.numero_animais) <= 0 ||
    parseFloat(animalCopy.preco_venda_arroba) <= 0 ||
    parseFloat(animalCopy.preco_arroba_adquirida) <= 0 ||
    parseFloat(animalCopy.rendimento_carcaca_inicial) <= 0 ||
    parseFloat(animalCopy.rendimento_carcaca_final) <= 0
  ) {
    if (activeCommit && commit) commit("setRelatorioFinanceiro", relatorioFinanceiro);
    return relatorioFinanceiro;
  }

  if (
    isNaN(parseFloat(animalCopy.numero_animais)) ||
    isNaN(parseFloat(animalCopy.preco_venda_arroba)) ||
    isNaN(parseFloat(animalCopy.preco_arroba_adquirida)) ||
    isNaN(parseFloat(animalCopy.rendimento_carcaca_inicial)) ||
    isNaN(parseFloat(animalCopy.rendimento_carcaca_final))
  ) {
    if (activeCommit && commit) commit("setRelatorioFinanceiro", relatorioFinanceiro);
    return relatorioFinanceiro;
  }

  if (
    (animalCopy.tipo === CRESCIMENTO_TERMINACAO ||
      (animalCopy.tipo === VACA && animalCopy.estado_fisiologico === NAO_GESTANTE)) &&
    animalCopy.peso_corporal_inicial &&
    animalCopy.tipo
  ) {
    relatorioFinanceiro = deepClone(relatorioFinanceiroModelo);

    animalCopy.numero_animais = parseInt(
      !animalCopy.numero_animais ? 1 : animalCopy.numero_animais
    );

    if (
      animalCopy.numero_animais > 0 &&
      animalCopy.preco_venda_arroba > 0 &&
      animalCopy.preco_arroba_adquirida > 0 &&
      animalCopy.rendimento_carcaca_inicial > 0 &&
      animalCopy.rendimento_carcaca_final > 0
    ) {
      const precoArrobaAdquirida = animalCopy.preco_arroba_adquirida;
      const precoVendaArroba = animalCopy.preco_venda_arroba;

      const custoRacao =
        (custo === null || !custo) && state
          ? state.app.composicao_racao.CUSTO.total
          : parseFloat(custo).toFixed(3);

      const GMD = gmd === null ? animalCopy.ganho_medio_diario : gmd;

      const PCI = animalCopy.peso_corporal_inicial || animalCopy.peso_corporal_medio;
      const PCF = animalCopy.peso_corporal_final || animalCopy.peso_corporal_medio;

      const RCI = parseFloat(animalCopy.rendimento_carcaca_inicial / 100);
      const RCF = parseFloat(animalCopy.rendimento_carcaca_final / 100);

      const N = animalCopy.numero_animais;

      const cmo = animalCopy.custo_mao_de_obra;
      const cen = animalCopy.custo_energia;
      const cmaq = animalCopy.custo_maquinario;
      const cmed = animalCopy.custo_medicamentos;
      const cex = animalCopy.custo_extra;
      const taxaJurosMes = animalCopy.taxa_juros_mensal;

      const outrosCustosDia = (cmo + cen + cmaq + cmed + cex) / 30.42 / N;

      const IA = ((PCI * RCI) / 15) * N * precoArrobaAdquirida;
      const pesoDeVenda = (PCF * RCF) / 15;
      const DCONF = Math.ceil(Math.abs(PCI - PCF) / GMD);
      const TAP = ((PCF - PCI) * RCF) / 15;
      const GDC = (PCF * RCF - PCI * RCI) / 15;

      const custoAlimentacao = custoRacao * DCONF;
      const RBT = pesoDeVenda * precoVendaArroba;
      const CAR = (custoAlimentacao / RBT) * 100;

      const custoDiario =
        (parseFloat(custoRacao) || 0) + (parseFloat(outrosCustosDia) || 0);

      const custoOperacionalCAB = custoDiario * DCONF;
      const CTR = (custoOperacionalCAB / RBT) * 100;
      const CAP = custoOperacionalCAB / GDC;

      const custoCompra = ((PCI * RCI) / 15) * precoArrobaAdquirida;
      const COEFFinal = custoCompra + custoOperacionalCAB;

      const pontoEquilibrioPreco = COEFFinal / pesoDeVenda;

      const custoOportunidadeCapital =
        custoCompra * Math.pow(1 + taxaJurosMes / 100, DCONF / 30.42) -
        custoCompra +
        ((custoOperacionalCAB / 2) *
          Math.pow(1 + taxaJurosMes / 100, DCONF / 30.42) -
          custoOperacionalCAB / 2);

      const pontoEquilibrioPrecoCOP =
        (COEFFinal + custoOportunidadeCapital) / pesoDeVenda;

      const margemOperacional = precoVendaArroba - CAP;
      const margemOperacionalCAB = margemOperacional * GDC;

      const lucroCAB = RBT - COEFFinal - custoOportunidadeCapital;

      const taxaRetornoInvestimentoAno =
        (Math.pow((pesoDeVenda * precoVendaArroba) / COEFFinal, 1 / (DCONF / 365)) - 1) *
        100;

      const taxaRetornoInvestimentoAnoCOP =
        (Math.pow(
          (pesoDeVenda * precoVendaArroba - custoOportunidadeCapital) / COEFFinal,
          1 / (DCONF / 365)
        ) - 1) *
        100;

      const receitaTotalAProduzida =
        (((PCI * RCF) / 15 - (PCI * RCI) / 15) * precoVendaArroba +
          ((PCI * RCI) / 15) *
            (precoVendaArroba - precoArrobaAdquirida)) *
        N;

      const ganhoDiarioCarcaca =
        (PCF * RCF - PCI * RCI) / Math.max(1, parseInt(DCONF));

      const rendimentoDoGanho =
        ((PCF * RCF - PCI * RCI) / Math.max(1, parseInt(DCONF)) / GMD) * 100;

      let composicaoDieta = _.get(
        state,
        "app.composicao_dieta.CMS.alimentos",
        {}
      );

      let cmsTotal = Object.values(composicaoDieta).reduce(
        (acc, curr) => acc + curr.valor,
        0
      );

      const eficienciaBiologica = (cmsTotal * 15) / ganhoDiarioCarcaca;

      /* ======================================================
         BLOCO ADAPTADO PARA FUNCIONAR SEM dataVendaEstimada
      ====================================================== */

      // const today = normalizeScenarioDate(new Date());

      // const dataNormalizada = dataVendaEstimada
      //   ? normalizeScenarioDate(dataVendaEstimada)
      //   : null;

      // const possuiDataValida =
      //   dataNormalizada instanceof Date &&
      //   !isNaN(dataNormalizada.getTime());

      // const dataEscolhida = possuiDataValida
      //   ? new Date(dataNormalizada.getTime())
      //   : new Date(today.getTime());

      // if (!possuiDataValida) {
      //   dataEscolhida.setDate(dataEscolhida.getDate() + DCONF);
      // }

      // const diasCalculados = daysBetweenDates(today, dataEscolhida);

      // const diasAteVendaEscolhida = Math.max(
      //   0,
      //   Math.min(DCONF, diasCalculados)
      // );

      // const progressoVenda = DCONF > 0 ? diasAteVendaEscolhida / DCONF : 0;

      // const rendimentoCarcacaEscolhido =
      //   RCI + (RCF - RCI) * progressoVenda;

      // const pesoVivoEscolhido =
      //   PCI + GMD * diasAteVendaEscolhida;

      // const arrobasHoje = (PCI * RCI) / 15;

      // const arrobasDataEscolhida =
      //   (pesoVivoEscolhido * rendimentoCarcacaEscolhido) / 15;

      // const receitaSeVenderHoje =
      //   arrobasHoje * precoVendaArroba;

      // const receitaEstimadaDataEscolhida =
      //   arrobasDataEscolhida * precoVendaArroba;

      // const custoManterAteData =
      //   custoDiario * diasAteVendaEscolhida;

      // const custoOportunidadeAteData =
      //   custoCompra *
      //     Math.pow(
      //       1 + taxaJurosMes / 100,
      //       diasAteVendaEscolhida / 30.42
      //     ) -
      //   custoCompra +
      //   ((custoManterAteData / 2) *
      //     Math.pow(
      //       1 + taxaJurosMes / 100,
      //       diasAteVendaEscolhida / 30.42
      //     ) -
      //     custoManterAteData / 2);

      // const saldoLiquidoEsperar =
      //   receitaEstimadaDataEscolhida -
      //   receitaSeVenderHoje -
      //   custoManterAteData -
      //   custoOportunidadeAteData;

      /* ====================================================== */

      relatorioFinanceiro.IAE.N.valor = parseInt(N);
      relatorioFinanceiro.IAE.DCONF.valor = parseInt(DCONF);
      relatorioFinanceiro.IAE.PESO_DE_VENDA.valor = pesoDeVenda;
      relatorioFinanceiro.IAE.TAP.valor = TAP;
      relatorioFinanceiro.IAE.GDC.valor = GDC;
      relatorioFinanceiro.IAE.RG.valor = rendimentoDoGanho;
      relatorioFinanceiro.IAE.GDiarioC.valor = ganhoDiarioCarcaca;
      relatorioFinanceiro.IAE.CUSTO_DIARIO.valor = custoDiario;
      relatorioFinanceiro.IAE.COCAB.valor = custoOperacionalCAB;
      relatorioFinanceiro.IAE.CAR.valor = CAR;
      relatorioFinanceiro.IAE.CTR.valor = CTR;
      relatorioFinanceiro.IAE.COT.valor = (COEFFinal / RBT) * 100;
      relatorioFinanceiro.IAE.CAP.valor = CAP;
      relatorioFinanceiro.IAE.PONTO_EQUILIBRIO_PRECO.valor = pontoEquilibrioPreco;
      relatorioFinanceiro.IAE.CUSTO_OPORTUNIDADE_CAPITAL.valor =
        custoOportunidadeCapital;
      // relatorioFinanceiro.IAE.DIAS_ATE_VENDA_ESCOLHIDA.valor =
      //   diasAteVendaEscolhida;
      // relatorioFinanceiro.IAE.RECEITA_SE_VENDER_HOJE.valor =
      //   receitaSeVenderHoje;
      // relatorioFinanceiro.IAE.RECEITA_ESTIMADA_DATA_ESCOLHIDA.valor =
      //   receitaEstimadaDataEscolhida;
      // relatorioFinanceiro.IAE.CUSTO_MANTER_ATE_DATA.valor =
      //   custoManterAteData;
      // relatorioFinanceiro.IAE.CUSTO_OPORTUNIDADE_ATE_DATA.valor =
      //   custoOportunidadeAteData;
      // relatorioFinanceiro.IAE.SALDO_LIQUIDO_ESPERAR.valor =
      //   saldoLiquidoEsperar;
      relatorioFinanceiro.IAE.PONTO_EQUILIBRIO_PRECO_COP.valor =
        pontoEquilibrioPrecoCOP;
      relatorioFinanceiro.IAE.MARGEM_OPERACIONAL.valor =
        margemOperacional;
      relatorioFinanceiro.IAE.MARGEM_OPERACIONAL_CAB.valor =
        margemOperacionalCAB;
      relatorioFinanceiro.IAE.RAT.valor = lucroCAB;
      relatorioFinanceiro.IAE.TAXA_RETORNO_INVESTIMENTO_ANO.valor =
        taxaRetornoInvestimentoAno;
      relatorioFinanceiro.IAE.TAXA_RETORNO_INVESTIMENTO_ANO_COP.valor =
        taxaRetornoInvestimentoAnoCOP;
      relatorioFinanceiro.IAE.EFICIENCIA_BIOLOGICA.valor =
        eficienciaBiologica;

      const receitaTotal = RBT * N;
      const oc = outrosCustosDia * N * DCONF;
      const custoOperacional = custoAlimentacao * N + oc;
      const margemBrutaTotal = receitaTotal - IA - custoOperacional;

      relatorioFinanceiro.ATC.IA.valor = IA;
      relatorioFinanceiro.ATC.CALT.valor = custoAlimentacao * N;
      relatorioFinanceiro.ATC.OC.valor = oc;
      relatorioFinanceiro.ATC.CT.valor = custoOperacional;
      relatorioFinanceiro.ATC.RBT.valor = receitaTotal;
      relatorioFinanceiro.ATC.RTA.valor = margemBrutaTotal;
      relatorioFinanceiro.ATC.RTPAA.valor = receitaTotalAProduzida;
    }
  }

  if (activeCommit && commit) {
    commit("setRelatorioFinanceiro", deepClone(relatorioFinanceiro));
  }

  return deepClone(relatorioFinanceiro);
};

export const relatorioFinanceiroCenariosGMD = (
  { commit, state },
  { animal, custo, perc = [0.8, 0.85, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15, 1.2], dataVendaEstimada = null }
) => {
  return relatorioFinanceiroCenarios(
    { commit, state },
    {
      animal,
      custo,
      dataVendaEstimada,
      scenarios: buildFinancialScenarios({
        key: "gmd",
        values: perc
      })
    }
  );
};

const SCENARIO_TARGET_GMD = "gmd";
const SCENARIO_TARGET_CUSTO = "custo";
const SCENARIO_TARGET_ANIMAL = "animal";

const SCENARIO_MODE_MULTIPLY = "multiply";
const SCENARIO_MODE_ADD = "add";
const SCENARIO_MODE_SET = "set";

const buildScenarioOperation = ({
  target = SCENARIO_TARGET_ANIMAL,
  field = null,
  mode = SCENARIO_MODE_MULTIPLY,
  value = 1
} = {}) => ({
  target,
  field,
  mode,
  value
});

const applyScenarioOperation = ({ animalBase, custoBase, gmdBase, operation }) => {
  const target = operation.target || SCENARIO_TARGET_ANIMAL;
  const mode = operation.mode || SCENARIO_MODE_MULTIPLY;
  const value = toScenarioNumber(operation.value);

  const readCurrentValue = () => {
    if (target === SCENARIO_TARGET_GMD) return gmdBase;
    if (target === SCENARIO_TARGET_CUSTO) return custoBase;
    return toScenarioNumber(_.get(animalBase, operation.field));
  };

  const writeNextValue = (nextValue) => {
    if (target === SCENARIO_TARGET_GMD) {
      return { animalBase, custoBase, gmdBase: nextValue };
    }
    if (target === SCENARIO_TARGET_CUSTO) {
      return { animalBase, custoBase: nextValue, gmdBase };
    }

    const animalAtualizado = deepClone(animalBase);
    _.set(animalAtualizado, operation.field, nextValue);
    return { animalBase: animalAtualizado, custoBase, gmdBase };
  };

  const currentValue = readCurrentValue();
  let nextValue = currentValue;

  if (mode === SCENARIO_MODE_SET) nextValue = value;
  else if (mode === SCENARIO_MODE_ADD) nextValue = currentValue + value;
  else nextValue = currentValue * value;

  return writeNextValue(nextValue);
};

const applyScenarioDefinition = ({ animal, custo, scenario }) => {
  let animalBase = deepClone(animal);
  let custoBase = toScenarioNumber(custo);
  let gmdBase = toScenarioNumber(animalBase.ganho_medio_diario);
  const operations = scenario.operations || [];

  operations.forEach((operation) => {
    const next = applyScenarioOperation({
      animalBase,
      custoBase,
      gmdBase,
      operation
    });

    animalBase = next.animalBase;
    custoBase = next.custoBase;
    gmdBase = next.gmdBase;
  });

  animalBase.useFormat = false;

  return {
    animal: animalBase,
    custo: custoBase,
    gmd: gmdBase
  };
};

export const relatorioFinanceiroScenarioPresets = {
  gmd: ({ values = [0.8, 0.85, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15, 1.2] } = {}) =>
    values.map((value) => ({
      id: value,
      type: "gmd",
      factor: value,
      operations: [
        buildScenarioOperation({
          target: SCENARIO_TARGET_GMD,
          mode: SCENARIO_MODE_MULTIPLY,
          value
        })
      ]
    })),
  precoVendaArroba: ({ values = [0.9, 0.95, 1.0, 1.05, 1.1] } = {}) =>
    values.map((value) => ({
      id: `preco_venda_${value}`,
      type: "animal",
      field: "preco_venda_arroba",
      factor: value,
      operations: [
        buildScenarioOperation({
          target: SCENARIO_TARGET_ANIMAL,
          field: "preco_venda_arroba",
          mode: SCENARIO_MODE_MULTIPLY,
          value
        })
      ]
    })),
  precoCompraArroba: ({ values = [0.9, 0.95, 1.0, 1.05, 1.1] } = {}) =>
    values.map((value) => ({
      id: `preco_compra_${value}`,
      type: "animal",
      field: "preco_arroba_adquirida",
      factor: value,
      operations: [
        buildScenarioOperation({
          target: SCENARIO_TARGET_ANIMAL,
          field: "preco_arroba_adquirida",
          mode: SCENARIO_MODE_MULTIPLY,
          value
        })
      ]
    })),
  custoRacao: ({ values = [0.9, 0.95, 1.0, 1.05, 1.1] } = {}) =>
    values.map((value) => ({
      id: `custo_racao_${value}`,
      type: "custo",
      factor: value,
      operations: [
        buildScenarioOperation({
          target: SCENARIO_TARGET_CUSTO,
          mode: SCENARIO_MODE_MULTIPLY,
          value
        })
      ]
    })),
  rendimentoCarcacaFinal: ({ values = [0.97, 1.0, 1.03] } = {}) =>
    values.map((value) => ({
      id: `rcf_${value}`,
      type: "animal",
      field: "rendimento_carcaca_final",
      factor: value,
      operations: [
        buildScenarioOperation({
          target: SCENARIO_TARGET_ANIMAL,
          field: "rendimento_carcaca_final",
          mode: SCENARIO_MODE_MULTIPLY,
          value
        })
      ]
    })),
  taxaJurosMensal: ({ values = [0.5, 1.0, 1.5] } = {}) =>
    values.map((value) => ({
      id: `juros_${value}`,
      type: "animal",
      field: "taxa_juros_mensal",
      value,
      operations: [
        buildScenarioOperation({
          target: SCENARIO_TARGET_ANIMAL,
          field: "taxa_juros_mensal",
          mode: SCENARIO_MODE_SET,
          value
        })
      ]
    }))
};

export const buildFinancialScenarios = ({ key = "gmd", values = null, scenarios = null } = {}) => {
  if (Array.isArray(scenarios) && scenarios.length) return scenarios;

  const presetFactory = relatorioFinanceiroScenarioPresets[key];
  if (!presetFactory) return [];

  return presetFactory({ values: values || undefined });
};

export const relatorioFinanceiroCenarios = (
  { commit, state },
  { animal, custo, scenarios = [], activeCommit = true, dataVendaEstimada = null }
) => {
  const cenarios = {};
  const animalBase = deepClone(animal);
  animalBase.useFormat = false;

  scenarios.forEach((scenario, index) => {
    const scenarioId =
      scenario.id !== undefined && scenario.id !== null ? scenario.id : index;
    const scenarioInput = applyScenarioDefinition({
      animal: animalBase,
      custo,
      scenario
    });

    cenarios[scenarioId] = relatorioFinanceiro(
      { commit, state },
      {
        animal: scenarioInput.animal,
        custo: scenarioInput.custo,
        gmd: scenarioInput.gmd,
        dataVendaEstimada,
        activeCommit: false
      }
    );
  });
  console.log({ scenarios, c: cenarios })

  if (activeCommit && commit) {
    commit("setRelatorioFinanceiroCenarios", deepClone(cenarios));
  }

  return deepClone(cenarios);
};

export const ocultarDietaRecebida = ({ state, commit }, dieta) => {
  if (!dieta.comp_user) return new Error("Dieta sem comp_user");
  return Vue.prototype.axios
    .post("compartilhar/me/ocultar", {
      id_compartilhamento: dieta.comp_user.id_compartilhamento,
      id_dieta: dieta.id,
    })
    .then((r) => {
      if (r.data.data.erro) return new Error(r.data.data.erro);
      return r.data.data;
    });
};
export const getDietasAtivas = ({ state, commit }) => {
  return new Promise((resolve, reject) => {
    return Vue.prototype.axios
      .post("dieta/getDietasAtivas")
      .then((r) => {
        let dietasAtivas = r.data.data.dietas;

        commit("setDietasAtivas", dietasAtivas);
        resolve(dietasAtivas);
        return dietasAtivas;
      })
      .catch((err) => {
        commit("getDefaultDietasAtivas");
        reject(err);
      });
  });
};
export const getDietasCompartilhadas = ({ state }) => {
  return new Promise((resolve, reject) => {
    return Vue.prototype.axios.post("compartilhar/me").then((res) => {
      state.app.dietasCompartilhadasComigo = res.data.data.dietas;
      state.app.predicoesCompartilhadasComigo = res.data.data.predicoes;
      return Vue.prototype.axios.post("compartilhar/dono").then((resDono) => {
        state.app.dietasCompartilhadasComOutros = resDono.data.data.dietas;
        state.app.predicoesCompartilhadasComOutros =
          resDono.data.data.predicoes;
        resolve({
          dono: resDono.data.data,
          comigo: res.data.data,
        });
      });
    });
  });
};

export const calcularDieta = ({ state }, params = null) => {
  let {
    exigenciasSelecionadasDefault = null,
    compatibilizar = false,
    vc = false,
    proporcaovc = null,
    elevaCMS = false,
  } = params === null ? {} : params;
  return new Promise((resolve, reject) => {
    var alimentos = Object.values(state.app.alimentos_selecionados);
    var exigencias = state.app.exigencias;
    var exigenciasSelecionadas =
      exigenciasSelecionadasDefault === null
        ? state.app.exigencias_selecionadas
        : exigenciasSelecionadasDefault;
    let idioma = state.app.idioma;
    var dataSimplex = {};
    let unidade = null;
    const unidades = state.app.unidades;
    // Nomes
    dataSimplex.nomes = {};
    alimentos.forEach((el) => {
      dataSimplex.nomes[el.id] = el.name;
    });

    // Custos
    dataSimplex.custo = {};
    alimentos.forEach((el) => {
      dataSimplex.custo[el.id] = parseMoney(el.custo, idioma);
    });
    dataSimplex.volumosos = [];
    dataSimplex.concentrados = [];
    dataSimplex.proporcaoVC = [];
    let { kpVolumoso, kpConcentrado } = chutaKP(state);

    // Nutrientes
    dataSimplex.nutrientes = {};
    for (let item of exigenciasSelecionadas) {
      dataSimplex.nutrientes[item] = {};
      let fator = exigencias[item].fator;
      alimentos.forEach((el) => {
        if (el.id === "ID_LEITE_VACA") el.id = ID_LEITE_VACA;
        let kp = 0;
        kp = el.volumoso ? kpVolumoso : kpConcentrado;
        let valor = round(el.nutrientes[exigencias[item].id]).toFixed(2);
        let ms = round(el.nutrientes["MS"]).toFixed(2);
        let fatorMs = exigencias["CMS"].fator;
        if (item === "NDT" && el.id === ID_LEITE_VACA) {
          let NDTAlimento = el.nutrientes.NDT_BRCORTE2016;
          valor = (NDTAlimento * (ms / 100)) / 100;
          // quantidade = parseFloat(animal.producao_de_leite)
        } else if (item === "EM") {
          if (el.id !== ID_LEITE_VACA && el.id !== "ID_LEITE_VACA") {
            let EMAlimento = parseFloat(el.nutrientes.EM_BRCORTE2016);
            let NDTAlimento = parseFloat(el.nutrientes.NDT_BRCORTE2016);
            let EDAlimento = parseFloat(el.nutrientes.ED_BRCORTE2016);
            if (NDTAlimento > 0 && !isNaN(parseFloat(NDTAlimento))) {
              EDAlimento = (NDTAlimento * 4.409) / 100;
            }
            if (EDAlimento > 0 && !isNaN(parseFloat(EDAlimento))) {
              EMAlimento = 0.9455 * EDAlimento - 0.303;
            }
            valor = EMAlimento * fator * ms * fatorMs;
          } else {
            valor = round(el.nutrientes["EM_BRCORTE2016"]).toFixed(5);
            valor = valor * fator;
          }
        } else if (item === "EL") {
          if (el.id !== ID_LEITE_VACA && el.id !== "ID_LEITE_VACA") {
            let EL = exigencias["EL"].minimo_raw;
            let EM = exigencias["EM"].minimo_raw;
            let kmp = EL / EM;
            let EMAlimento = parseFloat(el.nutrientes.EM_BRCORTE2016);
            let NDTAlimento = parseFloat(el.nutrientes.NDT_BRCORTE2016);
            let EDAlimento = parseFloat(el.nutrientes.ED_BRCORTE2016);
            if (NDTAlimento > 0 && !isNaN(parseFloat(NDTAlimento))) {
              EDAlimento = (NDTAlimento * 4.409) / 100;
            }
            if (EDAlimento > 0 && !isNaN(parseFloat(EDAlimento))) {
              EMAlimento = 0.9455 * EDAlimento - 0.303;
            }
            valor = kmp * EMAlimento * ms * fatorMs;
          } else {
            valor = valor * ms * exigencias["EL"].fator;
          }
        } else if (
          item === "PDR" ||
          item === "PNDR" ||
          item === "PM" ||
          item === "PB"
        ) {
          let alimento = el;
          let PBA = isNaN(round(alimento.nutrientes["PBA"] || alimento.nutrientes["PB_A"] || alimento.nutrientes["PB A"]))
            ? 0
            : round(alimento.nutrientes["PBA"] || alimento.nutrientes["PB_A"] || alimento.nutrientes["PB A"]);
          let PBB = isNaN(round(alimento.nutrientes["PBB"] || alimento.nutrientes["PB_B"] || alimento.nutrientes["PB B"]))
            ? 0
            : round(alimento.nutrientes["PBB"] || alimento.nutrientes["PB_B"] || alimento.nutrientes["PB B"]);
          let PBKD = isNaN(round(alimento.nutrientes["PBKD"]) || alimento.nutrientes["PB_kd"] || alimento.nutrientes["PB kd"])
            ? 0
            : round(round(alimento.nutrientes["PBKD"]) || alimento.nutrientes["PB_kd"] || alimento.nutrientes["PB kd"]);
          let PB = isNaN(round(alimento.nutrientes["PB"]))
            ? 0
            : round(alimento.nutrientes["PB"]);
          let PDRPB = isNaN(round(alimento.nutrientes["PDRPB"] || alimento.nutrientes["PDR_PB"] || alimento.nutrientes["PDR PB"]))
            ? 0
            : round(alimento.nutrientes["PDRPB"] || alimento.nutrientes["PDR_PB"] || alimento.nutrientes["PDR PB"]);
          let PDR = isNaN(round(alimento.nutrientes["PDR"]))
            ? 0
            : round(alimento.nutrientes["PDR"]);
          let PNDR = isNaN(round(alimento.nutrientes["PNDR"]))
            ? 0
            : round(alimento.nutrientes["PNDR"]);
          let PNDRPB = isNaN(round(alimento.nutrientes["PNDRPB"] || alimento.nutrientes["PNDR_PB"] || alimento.nutrientes["PNDR PB"]))
            ? 0
            : round(alimento.nutrientes["PNDRPB"] || alimento.nutrientes["PNDR_PB"] || alimento.nutrientes["PNDR PB"]);
          let PM = isNaN(round(alimento.nutrientes["PM"]))
            ? 0
            : round(alimento.nutrientes["PM"]);

          const proteinas = resolveProteinas({ alimento, kp });
          PDR = proteinas.PDR;
          PNDR = proteinas.PNDR;
          PB = proteinas.PB;
          PM = proteinas.PM;

          switch (item) {
            case "PNDR":
              valor = PNDR;
              break;
            case "PDR":
              valor = PDR;
              break;
            case "PM":
              valor = PM;
              break;
            case "PB":
              valor = PB;
              break;
          }
          valor = parseFloat(valor).toFixed(2);
          valor = valor * exigencias[item].fator * ms * fatorMs;
        } else if (item !== "CMS") {
          valor = valor * fator * ms * fatorMs;
        } else {
          valor = valor * fator;
        }
        dataSimplex.nutrientes[item][el.id] = valor;
      });
    }
    alimentos.forEach((el) => {
      if (el.volumoso && el.id !== ID_LEITE_VACA) {
        dataSimplex.volumosos.push(el.id);
      } else if (el.id !== ID_LEITE_VACA) {
        dataSimplex.concentrados.push(el.id);
      }
    });
    dataSimplex.proporcaoVC = state.app.vc.v;
    if (
      vc &&
      proporcaovc &&
      proporcaovc !== null &&
      parseFloat(proporcaovc.v) > 0
    ) {
      dataSimplex.proporcaoVC = parseFloat(proporcaovc.v);
    } else if (
      !state.app.fixavc ||
      dataSimplex.volumosos.length === 0 ||
      dataSimplex.concentrados.length === 0 ||
      dataSimplex.proporcaoVC <= 0
    ) {
      delete dataSimplex.proporcaoVC;
    } else if (
      typeof dataSimplex.proporcaoVC === "string" &&
      dataSimplex.proporcaoVC.split(":").length === 2
    ) {
      dataSimplex.proporcaoVC = parseInt(dataSimplex.proporcaoVC.split(":")[0]);
    }

    // Exigencias
    dataSimplex.exigencias = {};
    dataSimplex.exigencias.minimo = {};
    dataSimplex.exigencias.maximo = {};
    for (let item of exigenciasSelecionadas) {
      if (parseString(exigencias[item].minimo, idioma) > 0)
        dataSimplex.exigencias.minimo[item] = parseString(
          exigencias[item].minimo,
          idioma
        );
      else dataSimplex.exigencias.minimo[item] = NaN;
      if (parseString(exigencias[item].maximo, idioma) > 0)
        dataSimplex.exigencias.maximo[item] = parseString(
          exigencias[item].maximo,
          idioma
        );
      else dataSimplex.exigencias.maximo[item] = NaN;
    }

    // Restrições
    dataSimplex.restricoes = {};
    dataSimplex.restricoes.minimo = {};
    dataSimplex.restricoes.maximo = {};

    alimentos.forEach((el) => {
      if (compatibilizar) {
        if (el.quantidade && parseString(el.quantidade, idioma) > 0) {
          unidade = unidades.find((val) => val.id === el.unidade);
          let qtd = parseString(el.quantidade, idioma) * unidade.fator;
          dataSimplex.restricoes.minimo[el.id] = (qtd * 0.2).toFixed(5);
          dataSimplex.restricoes.maximo[el.id] = (qtd * 2.05).toFixed(5);
        } else {
          dataSimplex.restricoes.minimo[el.id] = 0;
          dataSimplex.restricoes.maximo[el.id] = 0;
        }
        return;
      }
      // converte a materia seca para natural das restrições dos alimentos;
      if (
        el.minimo &&
        el.maximo &&
        el.minimo !== "" &&
        el.maximo !== "" &&
        parseString(el.minimo, idioma) === 0 &&
        parseString(el.maximo, idioma) === 0
      ) {
        dataSimplex.restricoes.minimo[el.id] = 0;
        dataSimplex.restricoes.maximo[el.id] = 0;
      } else {
        if (el.minimo && el.minimo !== "") {
          dataSimplex.restricoes.minimo[el.id] =
            el.id === ID_LEITE_VACA
              ? parseString(el.minimo, idioma).toFixed(2) ===
                el.minimo_raw.toFixed(2)
                ? el.minimo_raw / (el.nutrientes["MS"] / 100)
                : parseString(el.minimo, idioma) / (el.nutrientes["MS"] / 100)
              : parseString(el.minimo, idioma) / (el.nutrientes["MS"] / 100);
        }
        if (
          el.maximo &&
          el.maximo !== "" &&
          parseString(el.maximo, idioma) !== 0 &&
          parseString(el.maximo, idioma) >= parseString(el.minimo, idioma)
        ) {
          dataSimplex.restricoes.maximo[el.id] =
            el.id === ID_LEITE_VACA
              ? parseString(el.maximo, idioma).toFixed(2) ===
                el.maximo_raw.toFixed(2)
                ? el.maximo_raw / (el.nutrientes["MS"] / 100)
                : parseString(el.maximo, idioma) / (el.nutrientes["MS"] / 100)
              : parseString(el.maximo, idioma) / (el.nutrientes["MS"] / 100);
        }
        // verificar se esta habilitado o %/MS
        if (state.app.restricoes.tipoEntrada === "/%") {
          dataSimplex.usaPorcentagemMS = true;
          let exCMSMax = exigencias["CMS"].ideal_raw;
          let exCMSIdeal = exCMSMax;
          for (let minmax of ["minimo", "maximo"]) {
            if (el.id === ID_LEITE_VACA) continue;
            if (el[minmax] && el[minmax] !== "") {
              dataSimplex.restricoes[minmax][el.id] =
                (dataSimplex.restricoes[minmax][el.id] * exCMSIdeal) / 100;
            }
          }
        }
      }
    });

    if (
      Object.keys(dataSimplex.restricoes.minimo).length === 0 &&
      Object.keys(dataSimplex.restricoes.maximo).length === 0
    ) {
      delete dataSimplex.restricoes.minimo;
    } else if (!Object.keys(dataSimplex.restricoes.minimo).length) {
      delete dataSimplex.restricoes.minimo;
    } else if (!Object.keys(dataSimplex.restricoes.maximo).length) {
      delete dataSimplex.restricoes.maximo;
    } else {
      for (let alimentoId in dataSimplex.restricoes.minimo) {
        if (
          dataSimplex.restricoes.minimo[alimentoId] <= 0 &&
          dataSimplex.restricoes.maximo[alimentoId] <= 0 &&
          !compatibilizar
        ) {
          for (let item of exigenciasSelecionadas) {
            delete dataSimplex.nutrientes[item][alimentoId];
          }
          delete dataSimplex.nomes[alimentoId];
          if (dataSimplex.proporcaoVC) {
            let val = dataSimplex.volumosos.findIndex(
              (e) => e.id === alimentoId
            );
            if (val) delete dataSimplex.volumosos[val];
            else {
              val = dataSimplex.concentrados.findIndex(
                (e) => e.id === alimentoId
              );
              if (val) delete dataSimplex.concentrados[val];
            }
          }
          delete dataSimplex.restricoes.minimo[alimentoId];
          delete dataSimplex.restricoes.maximo[alimentoId];
          delete dataSimplex.custo[alimentoId];
        }
        if (
          dataSimplex.restricoes.minimo[alimentoId] === undefined ||
          dataSimplex.restricoes.minimo[alimentoId] === 0
        )
          delete dataSimplex.restricoes.minimo[alimentoId];
        if (
          dataSimplex.restricoes.maximo[alimentoId] === undefined ||
          dataSimplex.restricoes.maximo[alimentoId] === 0
        )
          delete dataSimplex.restricoes.maximo[alimentoId];
      }
    }
    if (elevaCMS) {
      dataSimplex.exigencias.maximo["CMS"] *= 10;
    }
    resolve(dataSimplex);
  });
};
export const processaResultadoDieta = ({ commit, state, dispatch }, data) => {
  normaliza(state);
  return new Promise((resolve, reject) => {
    let dieta = {};
    try {
      dieta = JSON.parse(data.replace(/'/g, '"'));
    } catch (e) {
      dieta = JSON.parse(data.replace(/'/g, '"').replace("inf", '"inf"'));
    }
    state.app.resultado_formulacao = dieta;
    if (
      (dieta.status !== 1 && dieta.status !== 2) ||
      data.errors ||
      !dieta.nutrientes
    ) {
      resolve({ status: false, dieta: dieta });
    } else {
      const leitePosition = state.app.alimentos_selecionados.findIndex(
        (elm) => elm.id === ID_LEITE_VACA
      );
      let alimentosOrdenados =
        leitePosition !== -1
          ? [state.app.alimentos_selecionados[leitePosition]]
          : [];
      state.app.categorias_alimentos.forEach((cat) =>
        cat.derivados
          .filter(
            (al) =>
              state.app.alimentos_selecionados.findIndex(
                (alm) => alm.id === al.id
              ) !== -1
          )
          .sort((a, b) =>
            replaceSpecialChars(a.name) < replaceSpecialChars(b.name)
              ? -1
              : replaceSpecialChars(a.name) > replaceSpecialChars(b.name)
                ? 1
                : 0
          )
          .forEach((al) => {
            al.volumoso =
              al.tipo === 0 ||
              al.tipo === 7 ||
              al.tipo === 6 ||
              al.tipo === 1 ||
              SUBPRODUTOS_VOLUMOSOS.includes(al.id);

            alimentosOrdenados.push(al);
          })
      );
      state.app.alimentos_selecionados = alimentosOrdenados;
      let idioma = state.app.idioma;
      let animal = state.app.animal_;
      let alimentos = Object.values(state.app.alimentos_selecionados);
      state.app.animal_.exigencias.useFormat = false;
      state.app.animal_.useFormat = false;
      let exigencias = state.app.exigencias;

      let exigenciasResultado = state.app.exigencias_resultados;
      let composicaoRacao = state.app.composicao_racao;
      let composicaoDieta = state.app.composicao_dieta;
      let excrecao = state.app.excrecao;
      let totalRacao = animal.total_racao_a_produzir;
      let aditivos = state.app.aditivos;
      let totalMN = 0;
      let totalMS = 0;
      let totalCusto = 0;
      let totalVolumoso = 0;
      let totalConcentrado = 0;

      for (let item in dieta.nutrientes.MN) {
        dieta.nutrientes.MN[item] = parseFloat(
          dieta.nutrientes.MN[item],
          idioma
        );
        totalMN = totalMN + dieta.nutrientes.MN[item];
      }
      if (totalMN <= 0) resolve({ status: false, dieta: dieta });
      for (let alIndex in state.app.alimentos_selecionados) {
        state.app.alimentos_selecionados[alIndex].quantidade = null;
        state.app.alimentos_selecionados[alIndex].unidade = "kg";
      }

      for (let item in dieta.nutrientes.MN) {
        if (
          dieta.nutrientes.MN[item] &&
          dieta.nutrientes.MN[item] !== 0 &&
          !isNaN(dieta.nutrientes.MN[item])
        ) {
          let value = dieta.nutrientes.MN[item];
          let alimento = alimentos.find((el) => {
            return el.id === item;
          });
          let alimentoIndex = alimentos.findIndex((el) => {
            return el.id === item;
          });
          if (alimento === null || alimento === undefined) continue;
          let materiaSeca =
            item !== ID_LEITE_VACA
              ? parseString(alimento.nutrientes["MS"], idioma) * value * 0.01
              : alimento.nutrientes["MS"] * value * 0.01;
          let custo =
            alimento.custo &&
              alimento.custo !== "R$0,00" &&
              alimento.custo !== "U$0.00"
              ? parseMoney(alimento.custo, idioma) * value
              : 0;
          let volumoso = alimento.volumoso;
          totalMS = totalMS + materiaSeca;
          totalCusto = totalCusto + custo;
          if (custo) {
            composicaoRacao.CUSTO.alimentos[item] = {};
            composicaoRacao.CUSTO.alimentos[item].id = item;
            composicaoRacao.CUSTO.alimentos[item].valor =
              composicaoRacao.CUSTO.alimentos[item].valorUnidade = custo;
            composicaoRacao.CUSTO.alimentos[item].unidadeEsq = ["R$", "$", "$"];
            composicaoRacao.CUSTO.alimentos[item].unidadeDir = ["", "", ""];
          }
          if (value) {
            composicaoRacao.MN.alimentos[item] = {};
            composicaoRacao.MN.alimentos[item].id = item;
            composicaoRacao.MN.alimentos[item].valor = value;
            let valor = value;
            let unidade = "kg";
            if (valor < 1) {
              valor = valor * 1000;
              unidade = "g";
              if (valor < 1) {
                valor = valor * 1000;
                unidade = "mg";
              }
            }
            composicaoRacao.MN.alimentos[item].valorUnidade = valor;
            composicaoRacao.MN.alimentos[item].unidadeEsq = ["", "", ""];
            composicaoRacao.MN.alimentos[item].unidadeDir = [
              unidade,
              unidade,
              unidade,
            ];

            composicaoRacao.MNprop.alimentos[item] = {};
            composicaoRacao.MNprop.alimentos[item].id = item;
            composicaoRacao.MNprop.alimentos[item].valor =
              composicaoRacao.MNprop.alimentos[item].valorUnidade =
              (value / totalMN) * 100;
            composicaoRacao.MNprop.alimentos[item].unidadeEsq = ["", "", ""];
            composicaoRacao.MNprop.alimentos[item].unidadeDir = ["%", "%", "%"];
            composicaoRacao.MN.alimentos[item].prop =
              composicaoRacao.MNprop.alimentos[item];
            composicaoRacao.MN.alimentos[item].valor_limited = valor
              .toFixed(2)
              .replace(
                ".",
                state.app.strings[state.app.idioma].separador_float
              );
            state.app.alimentos_selecionados[alimentoIndex].quantidade = valor
              .toFixed(2)
              .replace(
                ".",
                state.app.strings[state.app.idioma].separador_float
              );
            state.app.alimentos_selecionados[alimentoIndex].unidade = unidade;
          }
          if (materiaSeca) {
            composicaoRacao.MS.alimentos[item] = {};
            composicaoRacao.MS.alimentos[item].id = item;
            composicaoRacao.MS.alimentos[item].valor = materiaSeca;
            composicaoRacao.MS.alimentos[item].valor_limited =
              materiaSeca.toFixed(2);
            let valor = materiaSeca;
            let unidade = "kg";
            if (valor < 1) {
              valor = valor * 1000;
              unidade = "g";
              if (valor < 1) {
                valor = valor * 1000;
                unidade = "mg";
              }
            }
            composicaoRacao.MS.alimentos[item].valorUnidade = valor;
            composicaoRacao.MS.alimentos[item].unidadeEsq = ["", "", ""];
            composicaoRacao.MS.alimentos[item].unidadeDir = [
              unidade,
              unidade,
              unidade,
            ];
          }
          let peso = composicaoRacao.MS.alimentos[item].valor;
          if (volumoso && item !== ID_LEITE_VACA) totalVolumoso += peso;
          else if (item !== ID_LEITE_VACA && materiaSeca) {
            totalConcentrado += peso;
            composicaoRacao.MSConc.alimentos[item] = {};
            composicaoRacao.MSConc.alimentos[item].id = item;
            composicaoRacao.MSConc.alimentos[item].valor =
              composicaoRacao.MS.alimentos[item].valor;
            composicaoRacao.MSConc.alimentos[item].valorUnidade =
              composicaoRacao.MS.alimentos[item].valorUnidade;
            composicaoRacao.MSConc.alimentos[item].unidadeEsq =
              composicaoRacao.MS.alimentos[item].unidadeEsq;
            composicaoRacao.MSConc.alimentos[item].unidadeDir =
              composicaoRacao.MS.alimentos[item].unidadeDir;
          }
        } else {
          delete dieta.nutrientes.MN[item];
        }
      }
      totalMN = Object.values(composicaoRacao.MN.alimentos)
        .map((al) => al.valor)
        .reduce((a, b) => a + b, 0);
      totalMS = Object.values(composicaoRacao.MS.alimentos)
        .map((al) => al.valor)
        .reduce((a, b) => a + b, 0);
      let totalMSConc = Object.values(composicaoRacao.MSConc.alimentos)
        .map((al) => al.valor)
        .reduce((a, b) => a + b, 0);
      for (let item in dieta.nutrientes.MN) {
        if (dieta.nutrientes.MN[item] !== 0) {
          if (composicaoRacao.MS.alimentos[item]) {
            composicaoRacao.MSprop.alimentos[item] = {};
            composicaoRacao.MSprop.alimentos[item].id = item;
            composicaoRacao.MSprop.alimentos[item].valor =
              composicaoRacao.MSprop.alimentos[item].valorUnidade =
              (composicaoRacao.MS.alimentos[item].valor / totalMS) * 100;
            composicaoRacao.MSprop.alimentos[item].unidadeEsq = ["", "", ""];
            composicaoRacao.MSprop.alimentos[item].unidadeDir = ["%", "%", "%"];
            composicaoRacao.MS.alimentos[item].prop =
              composicaoRacao.MSprop.alimentos[item];
          }
          if (composicaoRacao.MSConc.alimentos[item]) {
            composicaoRacao.MSConcProp.alimentos[item] = {};
            composicaoRacao.MSConcProp.alimentos[item].id = item;
            composicaoRacao.MSConcProp.alimentos[item].valor =
              composicaoRacao.MSConcProp.alimentos[item].valorUnidade =
              (composicaoRacao.MSConc.alimentos[item].valor / totalMSConc) *
              100;
            composicaoRacao.MSConcProp.alimentos[item].unidadeEsq = [
              "",
              "",
              "",
            ];
            composicaoRacao.MSConcProp.alimentos[item].unidadeDir = [
              "%",
              "%",
              "%",
            ];
            composicaoRacao.MSConc.alimentos[item].prop =
              composicaoRacao.MSConcProp.alimentos[item];
          }
          if (composicaoRacao.MNprop.alimentos[item]) {
            composicaoRacao.totalRacao.alimentos[item] = {};
            composicaoRacao.totalRacao.alimentos[item].id = item;
            composicaoRacao.totalRacao.alimentos[item].valor =
              composicaoRacao.MNprop.alimentos[item].valorUnidade *
              totalRacao *
              0.01;
            let valor = composicaoRacao.totalRacao.alimentos[item].valor;
            let unidade = "kg";
            if (valor < 1) {
              valor = valor * 1000;
              unidade = "g";
              if (valor < 1) {
                valor = valor * 1000;
                unidade = "mg";
              }
            }
            composicaoRacao.totalRacao.alimentos[item].valorUnidade = valor;
            composicaoRacao.totalRacao.alimentos[item].unidadeEsq = [
              "",
              "",
              "",
            ];
            composicaoRacao.totalRacao.alimentos[item].unidadeDir = [
              unidade,
              unidade,
              unidade,
            ];
          }
        }
      }
      composicaoRacao.MN.totalBruto = totalMN;
      composicaoRacao.MS.totalBruto = totalMS;
      composicaoRacao.MSConc.totalBruto = totalMSConc;
      let unidade = "kg";
      if (totalMN < 1) {
        totalMN = totalMN * 1000;
        unidade = "g";
        if (totalMN < 1) {
          totalMN = totalMN * 1000;
          unidade = "mg";
        }
      }
      composicaoRacao.MN.unidadeDir = [unidade, unidade, unidade];
      unidade = "kg";
      if (totalMS < 1) {
        totalMS = totalMS * 1000;
        unidade = "g";
        if (totalMS < 1) {
          totalMS = totalMS * 1000;
          unidade = "mg";
        }
      }
      composicaoRacao.MS.unidadeDir = [unidade, unidade, unidade];
      unidade = "kg";
      if (totalMSConc < 1) {
        totalMSConc = totalMSConc * 1000;
        unidade = "g";
        if (totalMSConc < 1) {
          totalMSConc = totalMSConc * 1000;
          unidade = "mg";
        }
      }
      composicaoRacao.MSConc.unidadeDir = [unidade, unidade, unidade];
      composicaoRacao.MN.total = totalMN;
      composicaoRacao.MNprop.total = 100.0;
      composicaoRacao.MS.total = totalMS;
      composicaoRacao.MSprop.total = 100.0;
      composicaoRacao.MSConc.total = totalMSConc;
      composicaoRacao.MSConcProp.total = 100.0;
      composicaoRacao.CUSTO.total = totalCusto;
      composicaoRacao.totalRacao.total = totalRacao;
      composicaoRacao.proporcaoVC.v = (
        (totalVolumoso / (totalVolumoso + totalConcentrado)) *
        100
      ).toFixed(0);
      composicaoRacao.proporcaoVC.c = 100 - composicaoRacao.proporcaoVC.v;
      for (let ex in composicaoRacao) {
        if (
          composicaoRacao[ex].alimentos === undefined ||
          composicaoRacao[ex].alimentos === {}
        )
          return;
        let alimentosOrdenados =
          composicaoRacao[ex].alimentos[ID_LEITE_VACA] !== undefined
            ? { "0-0|0|0": composicaoRacao[ex].alimentos[ID_LEITE_VACA] }
            : {};

        state.app.categorias_alimentos.forEach((cat) => {
          if (Object.keys(composicaoRacao[ex].alimentos).length > 0) {
            cat.derivados
              .filter(
                (al) =>
                  Object.keys(composicaoRacao[ex].alimentos).find(
                    (alm) => alm === al.id
                  ) !== undefined
              )
              .sort((a, b) =>
                replaceSpecialChars(a.name) < replaceSpecialChars(b.name)
                  ? -1
                  : replaceSpecialChars(a.name) > replaceSpecialChars(b.name)
                    ? 1
                    : 0
              )
              .forEach((al) => {
                alimentosOrdenados[al.id] = JSON.parse(
                  JSON.stringify(composicaoRacao[ex].alimentos[al.id])
                );
                for (let prop in alimentosOrdenados[al.id]) {
                  composicaoRacao[ex].alimentos[al.id][prop] =
                    alimentosOrdenados[al.id][prop];
                }
                composicaoRacao[ex].alimentos[al.id] =
                  alimentosOrdenados[al.id];
              });
          }
        });
      }

      // Cálculo eficência alimentar

      // [MUDAR A FORMA DE CALCULAR O KP, POIS NESSE PONTO JÁ TEMOS A PROPORÇÃO VC]
      let { pesoConcentrados, pesoVolumosos } = calculaPesoCV(state);
      const pesoAnimal = parseString(animal.peso_corporal_medio, idioma);
      var kpVolumoso =
        (2.365 +
          0.0214 * ((pesoVolumosos * 1000) / pesoAnimal) +
          0.0734 * ((pesoConcentrados * 1000) / pesoAnimal) +
          0.069 * pesoVolumosos) /
        100;
      var kpConcentrado =
        (1.169 +
          0.1375 * ((pesoVolumosos * 1000) / pesoAnimal) +
          0.1721 * ((pesoConcentrados * 1000) / pesoAnimal)) /
        100;
      // let { kpVolumoso, kpConcentrado } = chutaKP(state)
      // [MUDAR A FORMA DE CALCULAR O KP, POIS NESSE PONTO JÁ TEMOS A PROPORÇÃO VC]

      // aditivos
      for (let aditivo of aditivos) {
        let unidade = "";
        composicaoDieta[aditivo.id] = { ...aditivo };
        switch (aditivo.unidade) {
          case "mg/kg":
            unidade = ["mg/dia", "mg/day", "mg/día"];
            break;
          case "g/kg":
            unidade = ["g/dia", "g/day", "g/día"];
            break;
          case "%/MS":
            unidade = ["kg/dia", "kg/day", "kg/día"];
            break;
        }
        composicaoDieta[aditivo.id].unidade = unidade;
        composicaoDieta[aditivo.id].alimentos = {};
      }
      for (let item in dieta.nutrientes.MN) {
        if (item !== "None") {
          let quantidade = round(
            dieta.nutrientes.MN[item] >= 0.01
              ? dieta.nutrientes.MN[item].toFixed(2)
              : dieta.nutrientes.MN[item] >= 0.001
                ? dieta.nutrientes.MN[item].toFixed(3)
                : dieta.nutrientes.MN[item].toFixed(6)
          );
          let alimento = alimentos.find((el) => {
            return el.id === item;
          });
          if (alimento && alimento.id === "ID_LEITE_VACA")
            alimento.id = ID_LEITE_VACA;

          for (let exigencia in exigencias) {
            if (quantidade > 0) {
              let valor = round(
                alimento.nutrientes[exigencias[exigencia].id]
              ).toFixed(2);
              let ms = round(alimento.nutrientes.MS).toFixed(2);
              let fatorMs = exigencias.CMS.fator;
              let kp = alimento.volumoso ? kpVolumoso : kpConcentrado;
              if (exigencia === "NDT" && alimento.id === ID_LEITE_VACA) {
                let NDTAlimento = round(
                  alimento.nutrientes.NDT_BRCORTE2016
                ).toFixed(2);
                valor = (NDTAlimento * (ms / 100)) / 100;
                quantidade = parseFloat(animal.producao_de_leite);
              } else if (exigencia === "EM") {
                if (alimento.id !== ID_LEITE_VACA) {
                  let EMAlimento = parseFloat(
                    alimento.nutrientes.EM_BRCORTE2016
                  );
                  let NDTAlimento = parseFloat(
                    alimento.nutrientes.NDT_BRCORTE2016
                  );
                  let EDAlimento = parseFloat(
                    alimento.nutrientes.ED_BRCORTE2016
                  );
                  if (NDTAlimento > 0 && !isNaN(parseFloat(NDTAlimento))) {
                    EDAlimento = (NDTAlimento * 4.409) / 100;
                  }
                  if (EDAlimento > 0 && !isNaN(parseFloat(EDAlimento))) {
                    EMAlimento = 0.9455 * EDAlimento - 0.303;
                  }
                  valor =
                    EMAlimento * exigencias[exigencia].fator * ms * fatorMs;
                } else {
                  valor = round(
                    alimento.nutrientes["EM_BRCORTE2016"]
                  ).toFixed(5);
                  valor = valor * ms * exigencias[exigencia].fator;
                }
              } else if (exigencia === "EL") {
                if (alimento.id !== ID_LEITE_VACA) {
                  let EL = exigencias.EL.minimo_raw;
                  let EM = exigencias.EM.minimo_raw;
                  let kmp = EL / EM;
                  // let EMAlimento = (alimento.id === '889-52|0|600') ? 3.29 : (round(alimento.nutrientes.EM_BRCORTE2016).toFixed(2))
                  let EMAlimento = parseFloat(
                    alimento.nutrientes.EM_BRCORTE2016
                  );
                  let NDTAlimento = parseFloat(
                    alimento.nutrientes.NDT_BRCORTE2016
                  );
                  let EDAlimento = parseFloat(
                    alimento.nutrientes.ED_BRCORTE2016
                  );
                  if (NDTAlimento > 0 && !isNaN(parseFloat(NDTAlimento))) {
                    EDAlimento = (NDTAlimento * 4.409) / 100;
                  }
                  if (EDAlimento > 0 && !isNaN(parseFloat(EDAlimento))) {
                    EMAlimento = 0.9455 * EDAlimento - 0.303;
                  }
                  valor = kmp * EMAlimento * ms * fatorMs;
                } else {
                  valor = round(
                    alimento.nutrientes[exigencias[exigencia].id]
                  ).toFixed(5);
                  valor = valor * ms * exigencias[exigencia].fator;
                }
              } else if (
                exigencia === "PDR" ||
                exigencia === "PNDR" ||
                exigencia === "PM" ||
                exigencia === "PB"
              ) {
                let PBA = isNaN(round(alimento.nutrientes["PBA"]))
                  ? 0
                  : round(alimento.nutrientes["PBA"]);
                let PBB = isNaN(round(alimento.nutrientes["PBB"]))
                  ? 0
                  : round(alimento.nutrientes["PBB"]);
                let PBKD = isNaN(round(alimento.nutrientes["PBKD"]))
                  ? 0
                  : round(alimento.nutrientes["PBKD"]);
                let PB = isNaN(round(alimento.nutrientes["PB"]))
                  ? 0
                  : round(alimento.nutrientes["PB"]);
                let PDRPB = isNaN(round(alimento.nutrientes["PDRPB"] || alimento.nutrientes["PDR_PB"] || alimento.nutrientes["PDR PB"]))
                  ? 0
                  : round(alimento.nutrientes["PDRPB"] || alimento.nutrientes["PDR_PB"] || alimento.nutrientes["PDR PB"]);
                let PDR = isNaN(round(alimento.nutrientes["PDR"]))
                  ? 0
                  : round(alimento.nutrientes["PDR"]);
                let PNDR = isNaN(round(alimento.nutrientes["PNDR"]))
                  ? 0
                  : round(alimento.nutrientes["PNDR"]);
                let PNDRPB = isNaN(round(alimento.nutrientes["PNDRPB"] || alimento.nutrientes["PNDR_PB"] || alimento.nutrientes["PNDR PB"]))
                  ? 0
                  : round(alimento.nutrientes["PNDRPB"] || alimento.nutrientes["PNDR_PB"] || alimento.nutrientes["PNDR PB"]);
                let PM = isNaN(round(alimento.nutrientes["PM"]))
                  ? 0
                  : round(alimento.nutrientes["PM"]);

                const proteinas = resolveProteinas({ alimento, kp });
                PDR = proteinas.PDR;
                PNDR = proteinas.PNDR;
                PB = proteinas.PB;
                PM = proteinas.PM;

                switch (exigencia) {
                  case "PNDR":
                    valor = PNDR;
                    break;
                  case "PDR":
                    valor = PDR;
                    break;
                  case "PM":
                    valor = PM;
                    break;
                  case "PB":
                    valor = PB;
                    break;
                }

                valor = parseFloat(valor);
                valor = valor * exigencias[exigencia].fator * ms * fatorMs;
              } else if (exigencia !== "CMS") {
                valor = valor * exigencias[exigencia].fator * ms * fatorMs;
              } else {
                valor = valor * exigencias[exigencia].fator;
              }
              valor = valor * quantidade;
              if (valor > 0) {
                composicaoDieta[exigencia].alimentos[item] = {};
                composicaoDieta[exigencia].alimentos[item].valor = valor;
                let unidade = "kg";
                if (exigencia === "CMS") {
                  if (valor < 1) {
                    valor = valor * 1000;
                    unidade = "g";
                    if (valor < 1) {
                      valor = valor * 1000;
                      unidade = "mg";
                    }
                  }
                  composicaoDieta[exigencia].alimentos[item].valorUnidade =
                    valor;
                  composicaoDieta[exigencia].alimentos[item].unidade = [
                    unidade + "/dia",
                    unidade + "/day",
                    unidade + "/día",
                  ];
                } else {
                  composicaoDieta[exigencia].alimentos[item].valorUnidade =
                    valor;
                  composicaoDieta[exigencia].alimentos[item].unidade =
                    exigencias[exigencia].unidade;
                }
                composicaoDieta[exigencia].alimentos[item].id = item;
              }
              // exigencias extras
              if (exigencia === "FDN") {
                let AMIDO = alimento.nutrientes["AMIDO"]
                  ? parseFloat(alimento.nutrientes["AMIDO"])
                  : 0;
                let FDNfe = alimento.nutrientes["FDNfe"]
                  ? parseFloat(alimento.nutrientes["FDNfe"])
                  : 0;
                AMIDO = AMIDO * 0.01 * quantidade * ms * fatorMs;
                FDNfe = FDNfe * 0.01 * quantidade * ms * fatorMs;
                if (
                  composicaoDieta["AMIDO"] === undefined &&
                  composicaoDieta["FDNfe"] === undefined
                ) {
                  composicaoDieta["AMIDO"] = {};
                  composicaoDieta["AMIDO"].id = "AMIDO";
                  composicaoDieta["AMIDO"].alimentos = {};
                  composicaoDieta["FDNfe"] = {};
                  composicaoDieta["FDNfe"].id = "FDNfe";
                  composicaoDieta["FDNfe"].alimentos = {};
                }
                if (AMIDO > 0) {
                  let ex = "AMIDO";
                  let valor = AMIDO;
                  composicaoDieta[ex].alimentos[item] = {};
                  composicaoDieta[ex].alimentos[item].valor = valor;
                  composicaoDieta[ex].alimentos[item].valorUnidade = valor;
                  composicaoDieta[ex].alimentos[item].unidade =
                    exigenciasResultado[exigencia].unidade;

                  composicaoDieta[ex].alimentos[item].id = item;
                }
                if (FDNfe > 0) {
                  let ex = "FDNfe";
                  let valor = FDNfe;
                  composicaoDieta[ex].alimentos[item] = {};
                  composicaoDieta[ex].alimentos[item].valor = valor;

                  composicaoDieta[ex].alimentos[item].valorUnidade = valor;
                  composicaoDieta[ex].alimentos[item].unidade =
                    exigenciasResultado[exigencia].unidade;

                  composicaoDieta[ex].alimentos[item].id = item;
                }
              }
            }
          }
          // aditivos
          for (let aditivo of aditivos) {
            let valor = round(alimento.nutrientes[aditivo.id]).toFixed(2);
            let ms = round(alimento.nutrientes.MS).toFixed(2);
            let fatorMS = exigencias.CMS.fator;
            let prop = alimento.nutrientes[aditivo.id]
              ? parseFloat(alimento.nutrientes[aditivo.id])
              : 0;
            let fator = 1;
            let unidade = "";
            switch (aditivo.unidade) {
              case "mg/kg":
                fator = 1;
                unidade = ["mg/dia", "mg/day", "mg/día"];
                break;
              case "g/kg":
                fator = 10;
                unidade = ["g/dia", "g/day", "g/día"];
                break;
              case "%/MS":
                fator = 0.01;
                unidade = ["kg/dia", "kg/day", "kg/día"];
                break;
            }
            if (quantidade > 0 && !isNaN(quantidade) && prop > 0) {
              valor = prop * fator * quantidade * ms * fatorMS;
              composicaoDieta[aditivo.id].alimentos[alimento.id] = {};
              composicaoDieta[aditivo.id].alimentos[alimento.id].valor = valor;
              composicaoDieta[aditivo.id].alimentos[alimento.id].valorUnidade =
                valor;
              composicaoDieta[aditivo.id].alimentos[alimento.id].unidade =
                unidade;
              composicaoDieta[aditivo.id].alimentos[alimento.id].id =
                alimento.id;
            }
          }
          // ordenação da composição da dieta
          let cd = {};
          for (let ex in exigenciasResultado) {
            if (composicaoDieta[ex]) cd[ex] = composicaoDieta[ex];
          }
          for (let aditivo of aditivos) {
            if (composicaoDieta[aditivo.id])
              cd[aditivo.id] = composicaoDieta[aditivo.id];
          }
          composicaoDieta = cd;
        }
      }
      let produto =
        animal.tipo === VACA &&
          (animal.estado_fisiologico === LACTANTE ||
            animal.estado_fisiologico === GESTANTE_LACTANTE)
          ? animal.producao_de_leite
          : animal.ganho_medio_diario;
      let cmsTotal = Object.values(composicaoDieta.CMS.alimentos)
        .map((el) => el.valor)
        .reduce((cont, curr) => cont + curr, 0);
      composicaoRacao.eficienciaAlimentar.total = produto / cmsTotal;
      let litros =
        animal.tipo === VACA &&
          (animal.estado_fisiologico === LACTANTE ||
            animal.estado_fisiologico === GESTANTE_LACTANTE)
          ? animal.producao_de_leite
          : animal.ganho_medio_diario;
      let a = animal.peso_corporal_medio;
      let b = Object.values(composicaoDieta.NDT.alimentos)
        .map((el) => el.valor)
        .reduce((cont, curr) => cont + curr, 0);
      let c = Object.values(composicaoDieta.PB.alimentos)
        .map((el) => el.valor)
        .reduce((cont, curr) => cont + curr, 0);
      let d = Object.values(composicaoDieta.CMS.alimentos)
        .map((el) => el.valor)
        .reduce((cont, curr) => cont + curr, 0);
      let e = Object.values(composicaoDieta.P.alimentos)
        .map((el) => el.valor)
        .reduce((cont, curr) => cont + curr, 0);
      for (let ex in composicaoDieta) {
        let alimentosOrdenados =
          composicaoDieta[ex].alimentos[ID_LEITE_VACA] !== undefined
            ? { "0-0|0|0": composicaoDieta[ex].alimentos[ID_LEITE_VACA] }
            : {};

        state.app.categorias_alimentos.forEach((cat) => {
          if (Object.keys(composicaoDieta[ex].alimentos).length > 0) {
            cat.derivados
              .filter(
                (al) =>
                  Object.keys(composicaoDieta[ex].alimentos).find(
                    (alm) => alm === al.id
                  ) !== undefined
              )
              .sort((a, b) =>
                replaceSpecialChars(a.name) < replaceSpecialChars(b.name)
                  ? -1
                  : replaceSpecialChars(a.name) > replaceSpecialChars(b.name)
                    ? 1
                    : 0
              )
              .forEach((al) => {
                alimentosOrdenados[al.id] =
                  composicaoDieta[ex].alimentos[al.id];
              });
          }
        });
        composicaoDieta[ex].alimentos = alimentosOrdenados;
      }
      // for (let exigencia in exigencias) composicaoDieta[exigencia].alimentos = sortObject(composicaoDieta[exigencia].alimentos)
      for (let exigencia in exigenciasResultado)
        composicaoDieta[exigencia].total = Object.values(
          composicaoDieta[exigencia].alimentos
        )
          .map((el) => el.valor)
          .reduce((cont, curr) => cont + curr, 0);
      for (let aditivo of aditivos)
        composicaoDieta[aditivo.id].total = Object.values(
          composicaoDieta[aditivo.id].alimentos
        )
          .map((el) => el.valor)
          .reduce((cont, curr) => cont + curr, 0);
      for (let aditivo of aditivos)
        composicaoDieta[aditivo.id].mg =
          composicaoDieta[aditivo.id].total / cmsTotal;
      for (let exigencia in exigenciasResultado) {
        composicaoDieta[exigencia].cms_pv =
          exigencia === "CMS"
            ? (cmsTotal / animal.peso_corporal_medio) * 100
            : 0;
        composicaoDieta[exigencia].pcms =
          exigenciasResultado[exigencia].unidade[0] === "mg/dia" ||
            exigencia === "CMS" ||
            exigencia === "EL" ||
            exigencia === "EM"
            ? 0
            : parseFloat(composicaoDieta[exigencia].total.toFixed(2)) /
            exigenciasResultado[exigencia].fator /
            parseFloat(cmsTotal.toFixed(2));
        composicaoDieta[exigencia].mcal =
          exigencia === "EL" || exigencia === "EM"
            ? parseFloat(composicaoDieta[exigencia].total.toFixed(2)) /
            exigenciasResultado[exigencia].fator /
            parseFloat(cmsTotal.toFixed(2))
            : 0;

        composicaoDieta[exigencia].mg = exigenciasResultado[exigencia]
          .micromineral
          ? exigenciasResultado[exigencia].unidade[0] === "mg/dia"
            ? composicaoDieta[exigencia].total / cmsTotal
            : parseFloat(composicaoDieta[exigencia].total.toFixed(2)) /
            exigenciasResultado[exigencia].fator /
            parseFloat(cmsTotal.toFixed(2))
          : 0;
      }

      var concentradoNDTDieta = 0;
      var concentradoPBDieta = 0;
      Object.values(composicaoRacao.MSConc.alimentos).forEach((alimento) => {
        var alimentoId = alimento.id;
        if (composicaoDieta.NDT.alimentos[alimentoId]) {
          concentradoNDTDieta +=
            composicaoDieta.NDT.alimentos[alimentoId].valor;
        }

        if (composicaoDieta.PB.alimentos[alimentoId]) {
          concentradoPBDieta += composicaoDieta.PB.alimentos[alimentoId].valor;
        }
      });

      if (unidade === "g") {
        totalMSConc = totalMSConc / 1000;
        unidade === "kg"
      }
      if (unidade === "mg") {
        totalMSConc = totalMSConc / 1000000;
        unidade === "kg"
      }

      composicaoDieta.NDT.concentrado =
        (concentradoNDTDieta / totalMSConc) * 100;
      composicaoDieta.PB.concentrado =
        (concentradoPBDieta / 1000 / totalMSConc) * 100;
      composicaoDieta.CMS.concentrado = (totalMSConc / pesoAnimal) * 100;

      excrecao.excNFezes.valor = 2.55 + 0.048 * a - 3.47 * b + (0.3 / 6.25) * c;
      excrecao.excNUrina.valor = 3.26 + 3.68 * d + 0.18 * (c / 6.25);
      excrecao.excTotalN.valor =
        2.55 +
        0.048 * a -
        3.47 * b +
        (0.3 / 6.25) * c +
        (3.26 + 3.68 * d + 0.18 * (c / 6.25));
      excrecao.excTotalP.valor = 1.9 - 0.003 * a + 0.53 * e;
      excrecao.producaoMetano.valor = (37.523 + 19.3276 * d) * (16 / 22.41);
      excrecao.producaoMetanoProduto.valor =
        ((37.523 + 19.3276 * d) * (16 / 22.41)) / litros;
      let relatorioFinanceiro = state.app.relatorio_financeiro;
      if (
        (animal.tipo === CRESCIMENTO_TERMINACAO ||
          (animal.tipo === VACA &&
            animal.estado_fisiologico === NAO_GESTANTE)) &&
        animal.peso_corporal_inicial &&
        animal.tipo
      ) {
        animal.numero_animais = !animal.numero_animais
          ? null
          : animal.numero_animais;
        if (
          animal.numero_animais > 0 &&
          animal.preco_venda_arroba > 0 &&
          animal.preco_arroba_adquirida > 0 &&
          animal.rendimento_carcaca_inicial > 0 &&
          animal.rendimento_carcaca_final > 0
        ) {
          const precoArrobaAdquirida = animal.preco_arroba_adquirida;
          const precoVendaArroba = animal.preco_venda_arroba;
          const custoRacao = composicaoRacao.CUSTO.total;
          const GMD = animal.ganho_medio_diario;
          const PCI = animal.peso_corporal_inicial
            ? animal.peso_corporal_inicial
            : animal.peso_corporal_medio;
          const PCF = animal.peso_corporal_final
            ? animal.peso_corporal_final
            : animal.peso_corporal_medio;
          const RCI = parseFloat(animal.rendimento_carcaca_inicial / 100);
          const RCF = parseFloat(animal.rendimento_carcaca_final / 100);
          const N = parseInt(animal.numero_animais);

          const cmo = animal.custo_mao_de_obra;
          const cen = animal.custo_energia;
          const cmaq = animal.custo_maquinario;
          const cmed = animal.custo_medicamentos;
          const cex = animal.custo_extra;
          const taxaJurosMes = animal.taxa_juros_mensal;
          const outrosCustosDia = (cmo + cen + cmaq + cmed + cex) / 30.42 / N;

          const IA = ((PCI * RCI) / 15) * N * precoArrobaAdquirida;
          const pesoDeVenda = (PCF * RCF) / 15;
          const DCONF = Math.ceil(Math.abs(PCI - PCF) / GMD);
          const TAP = ((PCF - PCI) * RCF) / 15;
          const GDC = (PCF * RCF - PCI * RCI) / 15;

          const custoAlimentacao =
            parseFloat(parseFloat(custoRacao).toFixed(2)) * DCONF;
          const RBT = pesoDeVenda * precoVendaArroba;

          const CAR = (custoAlimentacao / RBT) * 100;
          const custoDiario = (parseFloat(custoRacao) || 0) + (parseFloat(outrosCustosDia) || 0);
          const custoOperacionalCAB = custoDiario * DCONF;
          const CTR = (custoOperacionalCAB / RBT) * 100;
          const CAP = custoOperacionalCAB / GDC;
          // --------
          const custoCompra = ((PCI * RCI) / 15) * precoArrobaAdquirida;
          const COEFFinal = custoCompra + custoOperacionalCAB;
          const pontoEquilibrioPreco = COEFFinal / pesoDeVenda;

          const custoOportunidadeCapital =
            custoCompra * Math.pow(1 + taxaJurosMes / 100, DCONF / 30.42) -
            custoCompra +
            ((custoOperacionalCAB / 2) *
              Math.pow(1 + taxaJurosMes / 100, DCONF / 30.42) -
              custoOperacionalCAB / 2);
          const pontoEquilibrioPrecoCOP =
            (COEFFinal + custoOportunidadeCapital) / pesoDeVenda;
          const margemOperacional = precoVendaArroba - CAP;
          const margemOperacionalCAB = margemOperacional * TAP;
          const lucroCAB = RBT - COEFFinal - custoOportunidadeCapital;
          const taxaRetornoInvestimentoAno =
            (Math.pow(
              (pesoDeVenda * precoVendaArroba) / COEFFinal,
              1 / (DCONF / 365)
            ) -
              1) *
            100;
          const taxaRetornoInvestimentoAnoCOP =
            (Math.pow(
              (pesoDeVenda * precoVendaArroba - custoOportunidadeCapital) /
              COEFFinal,
              1 / (DCONF / 365)
            ) -
              1) *
            100;
          const receitaTotalAProduzida =
            (((PCI * RCF) / 15 - (PCI * RCI) / 15) * precoVendaArroba +
              ((PCI * RCI) / 15) * (precoVendaArroba - precoArrobaAdquirida)) *
            N;
          const ganhoDiarioCarcaca =
            (PCF * RCF - PCI * RCI) / parseInt(Math.ceil(DCONF));
          const rendimentoDoGanho =
            ((PCF * RCF - PCI * RCI) / parseInt(Math.ceil(DCONF)) / GMD) * 100;
          const eficienciaBiologica = (cmsTotal * 15) / ganhoDiarioCarcaca;

          relatorioFinanceiro.IAE.N.valor = parseInt(N);
          relatorioFinanceiro.IAE.DCONF.valor = parseInt(Math.ceil(DCONF));
          relatorioFinanceiro.IAE.PESO_DE_VENDA.valor = parseFloat(pesoDeVenda);
          relatorioFinanceiro.IAE.TAP.valor = TAP;
          relatorioFinanceiro.IAE.GDC.valor = GDC;
          relatorioFinanceiro.IAE.RG.valor = rendimentoDoGanho;
          relatorioFinanceiro.IAE.GDiarioC.valor = ganhoDiarioCarcaca;
          relatorioFinanceiro.IAE.CUSTO_DIARIO.valor = custoDiario;
          relatorioFinanceiro.IAE.COCAB.valor = custoOperacionalCAB;
          relatorioFinanceiro.IAE.CAR.valor = CAR;
          relatorioFinanceiro.IAE.CTR.valor = CTR;
          relatorioFinanceiro.IAE.COT.valor = (COEFFinal / RBT) * 100;
          relatorioFinanceiro.IAE.CAP.valor = CAP;
          relatorioFinanceiro.IAE.PONTO_EQUILIBRIO_PRECO.valor =
            pontoEquilibrioPreco;
          relatorioFinanceiro.IAE.CUSTO_OPORTUNIDADE_CAPITAL.valor =
            custoOportunidadeCapital;
          relatorioFinanceiro.IAE.PONTO_EQUILIBRIO_PRECO_COP.valor =
            pontoEquilibrioPrecoCOP;
          relatorioFinanceiro.IAE.MARGEM_OPERACIONAL.valor = margemOperacional;
          relatorioFinanceiro.IAE.MARGEM_OPERACIONAL_CAB.valor =
            margemOperacionalCAB;
          relatorioFinanceiro.IAE.RAT.valor = lucroCAB;
          relatorioFinanceiro.IAE.TAXA_RETORNO_INVESTIMENTO_ANO.valor =
            taxaRetornoInvestimentoAno;
          relatorioFinanceiro.IAE.TAXA_RETORNO_INVESTIMENTO_ANO_COP.valor =
            taxaRetornoInvestimentoAnoCOP;
          relatorioFinanceiro.IAE.EFICIENCIA_BIOLOGICA.valor =
            eficienciaBiologica;

          const receitaTotal = RBT * N;
          const oc = outrosCustosDia * N * DCONF;
          const custoOperacional = custoAlimentacao * N + oc;
          const margemBrutaTotal = receitaTotal - IA - custoOperacional;
          relatorioFinanceiro.ATC.IA.valor = IA;
          relatorioFinanceiro.ATC.CALT.valor = custoAlimentacao * N;
          relatorioFinanceiro.ATC.OC.valor = oc;
          relatorioFinanceiro.ATC.CT.valor = custoOperacional;
          relatorioFinanceiro.ATC.RBT.valor = receitaTotal;
          relatorioFinanceiro.ATC.RTA.valor = margemBrutaTotal;
          relatorioFinanceiro.ATC.RTPAA.valor = receitaTotalAProduzida;
        }
      }
      state.app.animal_.exigencias.useFormat = true;
      state.app.animal_.useFormat = true;

      normaliza(state);
      commit("setComposicaoRacao", composicaoRacao);
      commit("setComposicaoDieta", composicaoDieta);
      commit("setExcrecao", excrecao);
      commit("setRelatorioFinanceiro", relatorioFinanceiro);
      dispatch("relatorioFinanceiroCenariosGMD", {
        animal: state.app.animal_,
        custo: totalCusto,
      });

      resolve({
        status: true,
        dieta: dieta,
        composicao_dieta: composicaoDieta,
        composicao_racao: composicaoRacao,
      });
    }
  });
};
export const saveDieta = (
  { commit, state },
  { name, dietaCarregada = null, idCliente = null, obs = null, forceBPClient = false }
) => {
  const saveDieta = (state, name, idCliente) => {
    let alChangedSession = JSON.parse(sessionStorage.getItem("AL_CHANGED"));
    let cliente = JSON.parse(localStorage.getItem("cliente"));
    let dietas = JSON.parse(localStorage.getItem("dietas__"));
    if (dietas === null || Object.keys(dietas).length === 0) {
      dietas = [];
    }
    let dietasEncontradas = dietas.find((n) => n.name === name);
    if (dietasEncontradas !== undefined) {
      name += new Date().getTime();
    }
    let dieta = {};
    // crfia objeto para animal;
    let animal = state.app.animal_.getMobileData();
    let resultado = {};
    for (let id in state.app.composicao_racao["MN"].alimentos) {
      if (alChangedSession !== null && alChangedSession[id]) {
        resultado[alChangedSession[id]] =
          state.app.composicao_racao["MN"].alimentos[id].valor;
      } else {
        resultado[id] = state.app.composicao_racao["MN"].alimentos[id].valor;
      }
    }
    let exigenciasSelecionadas = Array.from(state.app.exigencias_selecionadas);
    let exigenciasAlteradas = {};
    for (let exigencia in state.app.exigencias) {
      if (state.app.exigencias[exigencia].changed)
        exigenciasAlteradas[exigencia] =
          state.app.exigencias[exigencia].ideal_raw;
    }
    let restricoes = {};
    let alimentosSelecionados = [];
    let alimentosAlterados = {};
    // copia profunda
    let alsel = JSON.parse(JSON.stringify(state.app.alimentos_selecionados));
    let alimentosMudados = JSON.parse(
      localStorage.getItem("alimentos_mudados")
    );
    for (let a of Object.values(alsel)) {
      if (alimentosMudados && alimentosMudados !== null) {
        let als = alimentosMudados.filter(
          (alimento) => alimento.id_alimento_pai === a.id
        );
        if (als !== null && als[0]) {
          a.id_alimento_pai = a.id;
          a.id = als[0].id;
          a.name = als[0].name;
          a.name_en = als[0].name_en;
          a.name_es = als[0].name_es;
        }
      }

      a.minimo =
        a.minimo !== null &&
          typeof a.minimo === "string" &&
          a.minimo.trim() !== ""
          ? parseString(a.minimo, state.app.idioma)
          : a.minimo;
      a.maximo =
        a.maximo !== null &&
          typeof a.maximo === "string" &&
          a.maximo.trim() !== ""
          ? parseString(a.maximo, state.app.idioma)
          : a.maximo;
      a.custo =
        a.custo !== null && typeof a.custo === "string" && a.custo.trim() !== ""
          ? parseString(a.custo, state.app.idioma)
          : a.custo;
      if (alChangedSession !== null && alChangedSession[a.id]) {
        restricoes[alChangedSession[a.id]] = !a.alterado
          ? { minimo: a.minimo, maximo: a.maximo, custo: a.custo }
          : {
            minimo: a.minimo,
            maximo: a.maximo,
            custo: a.custo,
            nutrientes: a.nutrientes,
          };
        alimentosSelecionados.push(alChangedSession[a.id]);
        if (a.alterado) {
          alimentosAlterados[alChangedSession[a.id]] = a.nutrientes;
        }
      } else {
        restricoes[a.id] = !a.alterado
          ? { minimo: a.minimo, maximo: a.maximo, custo: a.custo }
          : {
            minimo: a.minimo,
            maximo: a.maximo,
            custo: a.custo,
            nutrientes: a.nutrientes,
          };
        alimentosSelecionados.push(a.id);
        if (a.alterado) {
          alimentosAlterados[a.id] = a.nutrientes;
        }
      }
    }
    let res = state.app.resultado_formulacao;
    let vc = state.app.fixavc
      ? {
        v: state.app.composicao_racao["proporcaoVC"].v,
        c: state.app.composicao_racao["proporcaoVC"].c,
      }
      : null;
    let tipoEntrada = state.app.restricoes.tipoEntrada;
    dieta = {
      nome: name,
      tipoEntrada: tipoEntrada,
      time: new Date().getTime(),
      custo: state.app.composicao_racao["CUSTO"].total,
      animal,
      alimentos_selecionados: alimentosSelecionados,
      alimentos_alterados: alimentosAlterados,
      restricoes,
      resultado,
      proporcao_volumoso: state.app.fixavc ? vc.v : null,
      fixa_proporcao_vc: state.app.fixavc,
      exigencias_selecionadas: exigenciasSelecionadas,
      exigencias_alteradas: exigenciasAlteradas,
      user_id: state.app.user.id,
      status: res.status ? res.status : 2,
      erro: res.erro_min ? res.erro_min : null,
      resultado_simplex: res,
      obs: obs,
    };
    if (forceBPClient) {
      dieta['id_cliente'] = null;
    } else if ((cliente && cliente !== null) || idCliente !== null) {
      dieta["id_cliente"] = idCliente !== null ? idCliente : cliente.id;
    }
    dietas.push(dieta);

    localStorage.setItem("dietas__", JSON.stringify(dietas));
    return dieta;
  };
  const updateDieta = (state, dietaCarregada, obs = null) => {
    let alChangedSession = JSON.parse(sessionStorage.getItem("AL_CHANGED"));
    let cliente = (dietaCarregada && dietaCarregada.id_cliente) ? dietaCarregada.id_cliente : JSON.parse(localStorage.getItem("cliente"));
    let dietas = JSON.parse(localStorage.getItem("dietas__"));
    if (dietas === null || Object.keys(dietas).length === 0) {
      dietas = [];
    }
    let dietasEncontradas = dietas.find((n) => n.name === name);
    if (dietasEncontradas !== undefined) {
      name += new Date().getTime();
    }
    let dieta = dietaCarregada;
    // crfia objeto para animal;
    let animal = state.app.animal_.getMobileData();
    let resultado = {};
    for (let id in state.app.composicao_racao["MN"].alimentos) {
      if (alChangedSession !== null && alChangedSession[id]) {
        resultado[alChangedSession[id]] =
          state.app.composicao_racao["MN"].alimentos[id].valor;
      } else {
        resultado[id] = state.app.composicao_racao["MN"].alimentos[id].valor;
      }
    }
    let exigenciasSelecionadas = Array.from(state.app.exigencias_selecionadas);
    let exigenciasAlteradas = {};
    for (let exigencia in state.app.exigencias) {
      if (state.app.exigencias[exigencia].changed)
        exigenciasAlteradas[exigencia] =
          state.app.exigencias[exigencia].ideal_raw;
    }
    let restricoes = {};
    let alimentosSelecionados = [];
    let alimentosAlterados = {};
    let alsel = Object.assign({}, state.app.alimentos_selecionados);
    let alimentosMudados = JSON.parse(
      localStorage.getItem("alimentos_mudados")
    );
    for (let a of Object.values(alsel)) {
      if (alimentosMudados && alimentosMudados !== null) {
        let als = alimentosMudados.filter(
          (alimento) => alimento.id_alimento_pai === a.id
        );
        if (als !== null && als[0]) {
          a.id_alimento_pai = a.id;
          a.id = als[0].id;
          a.name = als[0].name;
          a.name_en = als[0].name_en;
          a.name_es = als[0].name_es;
        }
      }

      a.minimo =
        a.minimo !== null &&
          typeof a.minimo === "string" &&
          a.minimo.trim() !== ""
          ? parseString(a.minimo, state.app.idioma)
          : a.minimo;
      a.maximo =
        a.maximo !== null &&
          typeof a.maximo === "string" &&
          a.maximo.trim() !== ""
          ? parseString(a.maximo, state.app.idioma)
          : a.maximo;
      a.custo =
        a.custo !== null && typeof a.custo === "string" && a.custo.trim() !== ""
          ? parseString(a.custo, state.app.idioma)
          : a.custo;
      if (alChangedSession !== null && alChangedSession[a.id]) {
        restricoes[alChangedSession[a.id]] = !a.alterado
          ? { minimo: a.minimo, maximo: a.maximo, custo: a.custo }
          : {
            minimo: a.minimo,
            maximo: a.maximo,
            custo: a.custo,
            nutrientes: a.nutrientes,
          };
        alimentosSelecionados.push(alChangedSession[a.id]);
        if (a.alterado) {
          alimentosAlterados[alChangedSession[a.id]] = a.nutrientes;
        }
      } else {
        restricoes[a.id] = !a.alterado
          ? { minimo: a.minimo, maximo: a.maximo, custo: a.custo }
          : {
            minimo: a.minimo,
            maximo: a.maximo,
            custo: a.custo,
            nutrientes: a.nutrientes,
          };
        alimentosSelecionados.push(a.id);
        if (a.alterado) {
          alimentosAlterados[a.id] = a.nutrientes;
        }
      }
    }
    let res = state.app.resultado_formulacao;
    let vc = state.app.fixavc
      ? {
        v: state.app.composicao_racao["proporcaoVC"].v,
        c: state.app.composicao_racao["proporcaoVC"].c,
      }
      : null;
    let tipoEntrada = state.app.restricoes.tipoEntrada;

    dieta = {
      nome: dietaCarregada.nome,
      tipoEntrada: tipoEntrada,
      time: new Date().getTime(),
      custo: state.app.composicao_racao["CUSTO"].total,
      animal,
      alimentos_selecionados: alimentosSelecionados,
      alimentos_alterados: alimentosAlterados,
      restricoes,
      resultado,
      proporcao_volumoso: state.app.fixavc ? vc.v : null,
      fixa_proporcao_vc: state.app.fixavc,
      exigencias_selecionadas: exigenciasSelecionadas,
      exigencias_alteradas: exigenciasAlteradas,
      user_id: dietaCarregada.user_id,
      id: dieta.id,
      status: res.status ? res.status : 2,
      erro: res.erro_min ? res.erro_min : null,
      resultado_simplex: res,
      obs: obs,
    };
    if (cliente && cliente !== null) {
      dieta["id_cliente"] = _.has(cliente, 'id') ? _.get(cliente, 'id') : cliente;
    }
    dietas.push(dieta);

    localStorage.setItem("dietas__", JSON.stringify(dietas));
    return dieta;
  };
  const predicao =
    dietaCarregada === null
      ? saveDieta(state, name, idCliente)
      : updateDieta(state, dietaCarregada, obs);

  return Vue.prototype.axios
    .post(
      "dieta/saveFD",
      dietaCarregada === null
        ? { dieta: JSON.stringify(predicao) }
        : {
          id: dietaCarregada.id,
          dieta: JSON.stringify(predicao),
          obs: dietaCarregada.obs,
        }
    )
    .then((res) => {
      let md5hash = createMD5(String(res.data.id));
      state.app.dieta_salva = {
        assinatura: md5hash,
        dieta: JSON.parse(res.data.dieta),
        id: res.data.id,
      };
      return res;
    });
};
export const saveDietaOculta = ({ commit, state }) => {
  const saveDieta = (state) => {
    let name = "";
    let dietas = JSON.parse(localStorage.getItem("dietas__"));
    let cliente = JSON.parse(localStorage.getItem("cliente"));
    if (dietas === null || Object.keys(dietas).length === 0) {
      dietas = [];
    }
    let dietasEncontradas = dietas.find((n) => n.name === name);
    if (dietasEncontradas !== undefined) {
      name += new Date().getTime();
    }
    let dieta = {};
    // crfia objeto para animal;
    let animal = state.app.animal_.getMobileData();
    let resultado = {};
    let alimentosMudados = JSON.parse(
      localStorage.getItem("alimentos_mudados")
    );
    for (let id in state.app.composicao_racao["MN"].alimentos) {
      if (alimentosMudados !== null) {
        let als = alimentosMudados.filter(
          (alimento) =>
            alimento.id_alimento_pai ===
            state.app.composicao_racao["MN"].alimentos.id
        );
        if (als !== null && als[0]) {
          state.app.composicao_racao["MN"].alimentos.id_alimento_pai =
            state.app.composicao_racao["MN"].alimentos.id;
          state.app.composicao_racao["MN"].alimentos.id = als[0].id;
          state.app.composicao_racao["MN"].alimentos.name = als[0].name;
          state.app.composicao_racao["MN"].alimentos.name_en = als[0].name_en;
          state.app.composicao_racao["MN"].alimentos.name_es = als[0].name_es;
          id = state.app.composicao_racao["MN"].alimentos.id;
        }
      }
      resultado[id] = state.app.composicao_racao["MN"].alimentos[id].valor;
    }
    let exigenciasSelecionadas = Array.from(state.app.exigencias_selecionadas);
    let exigenciasAlteradas = {};
    for (let exigencia in state.app.exigencias) {
      if (state.app.exigencias[exigencia].changed)
        exigenciasAlteradas[exigencia] =
          state.app.exigencias[exigencia].ideal_raw;
    }
    let restricoes = {};
    let alimentosSelecionados = [];
    let alimentosAlterados = {};
    // cópia profunda
    let alsel = JSON.parse(JSON.stringify(state.app.alimentos_selecionados));
    let numerosSemRepeticao = [];
    alsel.forEach((element) => {
      var index = numerosSemRepeticao.filter((d) => d.id == element.id);
      if (index.length == 0) {
        numerosSemRepeticao.push(element);
      }
    });

    for (let a of Object.values(numerosSemRepeticao)) {
      if (alimentosMudados !== null) {
        let als = alimentosMudados.filter(
          (alimento) => alimento.id_alimento_pai === a.id
        );
        if (als !== null && als[0]) {
          a.id_alimento_pai = a.id;
          a.id = als[0].id;
          a.name = als[0].name;
          a.name_en = als[0].name_en;
          a.name_es = als[0].name_es;
        }
      }
      a.minimo =
        a.minimo !== null &&
          typeof a.minimo === "string" &&
          a.minimo.trim() !== ""
          ? parseString(a.minimo, state.app.idioma)
          : a.minimo;
      a.maximo =
        a.maximo !== null &&
          typeof a.maximo === "string" &&
          a.maximo.trim() !== ""
          ? parseString(a.maximo, state.app.idioma)
          : a.maximo;
      a.custo =
        a.custo !== null && typeof a.custo === "string" && a.custo.trim() !== ""
          ? parseString(a.custo, state.app.idioma)
          : a.custo;
      restricoes[a.id] = !a.alterado
        ? { minimo: a.minimo, maximo: a.maximo, custo: a.custo }
        : {
          minimo: a.minimo,
          maximo: a.maximo,
          custo: a.custo,
          nutrientes: a.nutrientes,
        };
      alimentosSelecionados.push(a.id);
      if (a.alterado) {
        alimentosAlterados[a.id] = a.nutrientes;
      }
    }
    let res = state.app.resultado_formulacao;
    let vc = state.app.fixavc
      ? {
        v: state.app.composicao_racao["proporcaoVC"].v,
        c: state.app.composicao_racao["proporcaoVC"].c,
      }
      : null;
    let tipoEntrada = state.app.restricoes.tipoEntrada;
    dieta = {
      nome: name,
      time: new Date().getTime(),
      tipoEntrada: tipoEntrada,
      custo: state.app.composicao_racao["CUSTO"].total,
      animal,
      alimentos_selecionados: alimentosSelecionados,
      alimentos_alterados: alimentosAlterados,
      restricoes,
      resultado,
      proporcao_volumoso: state.app.fixavc ? vc.v : null,
      fixa_proporcao_vc: state.app.fixavc,
      exigencias_selecionadas: exigenciasSelecionadas,
      exigencias_alteradas: exigenciasAlteradas,
      user_id: state.app.user.id,
      status: res.status ? res.status : 2,
      erro: res.erro_min ? res.erro_min : null,
      resultado_simplex: res,
      oculta: true,
    };
    if (cliente && cliente !== null) {
      dieta["id_cliente"] = cliente.id;
    }
    dietas.push(dieta);
    localStorage.setItem("dietas__", JSON.stringify(dietas));
    return dieta;
  };
  const predicao = saveDieta(state, name);
  return Vue.prototype.axios
    .post("dieta/saveFD", { dieta: JSON.stringify(predicao) })
    .then((res) => {
      let md5hash = createMD5(String(res.data.id));
      state.app.dieta_salva = {
        assinatura: md5hash,
        dieta: JSON.parse(res.data.dieta),
        id: res.data.id,
      };
      return res;
    });
};
export const updateDieta = ({ commit, state }, name) => {
  const saveDieta = (state, name) => {
    let dietas = JSON.parse(localStorage.getItem("dietas__"));
    let cliente = JSON.parse(localStorage.getItem("cliente"));
    if (dietas === null || Object.keys(dietas).length === 0) {
      dietas = [];
    }
    let dietasEncontradas = dietas.find((n) => n.name === name);
    if (dietasEncontradas !== undefined) {
      name += new Date().getTime();
    }
    let dieta = {};
    // crfia objeto para animal;
    let animal = state.app.animal_.getMobileData();
    let resultado = {};
    for (let id in state.app.composicao_racao["MN"].alimentos) {
      resultado[id] = state.app.composicao_racao["MN"].alimentos[id].valor;
    }
    let exigenciasSelecionadas = Array.from(state.app.exigencias_selecionadas);
    let exigenciasAlteradas = {};
    for (let exigencia in state.app.exigencias) {
      if (state.app.exigencias[exigencia].changed)
        exigenciasAlteradas[exigencia] =
          state.app.exigencias[exigencia].ideal_raw;
    }
    let restricoes = {};
    let alimentosSelecionados = [];
    let alimentosAlterados = {};
    let alsel = Object.assign({}, state.app.alimentos_selecionados);
    let alimentosMudados = JSON.parse(
      localStorage.getItem("alimentos_mudados")
    );
    for (let a of Object.values(alsel)) {
      let als = alimentosMudados.filter(
        (alimento) => alimento.id_alimento_pai === a.id
      );
      if (als !== null && als[0]) {
        a.id_alimento_pai = a.id;
        a.id = als[0].id;
        a.name = als[0].name;
        a.name_en = als[0].name_en;
        a.name_es = als[0].name_es;
      }
      a.minimo =
        a.minimo !== null &&
          typeof a.minimo === "string" &&
          a.minimo.trim() !== ""
          ? parseString(a.minimo, state.app.idioma)
          : a.minimo;
      a.maximo =
        a.maximo !== null &&
          typeof a.maximo === "string" &&
          a.maximo.trim() !== ""
          ? parseString(a.maximo, state.app.idioma)
          : a.maximo;
      a.custo =
        a.custo !== null && typeof a.custo === "string" && a.custo.trim() !== ""
          ? parseString(a.custo, state.app.idioma)
          : a.custo;
      restricoes[a.id] = !a.alterado
        ? { minimo: a.minimo, maximo: a.maximo, custo: a.custo }
        : {
          minimo: a.minimo,
          maximo: a.maximo,
          custo: a.custo,
          nutrientes: a.nutrientes,
        };
      alimentosSelecionados.push(a.id);
      if (a.alterado) {
        alimentosAlterados[a.id] = a.nutrientes;
      }
    }
    let res = state.app.resultado_formulacao;
    let vc = state.app.fixavc
      ? {
        v: state.app.composicao_racao["proporcaoVC"].v,
        c: state.app.composicao_racao["proporcaoVC"].c,
      }
      : null;
    let tipoEntrada = state.app.restricoes.tipoEntrada;
    dieta = {
      nome: name,
      time: new Date().getTime(),
      tipoEntrada: tipoEntrada,
      custo: state.app.composicao_racao["CUSTO"].total,
      animal,
      alimentos_selecionados: alimentosSelecionados,
      alimentos_alterados: alimentosAlterados,
      restricoes,
      resultado,
      proporcao_volumoso: state.app.fixavc ? vc.v : null,
      fixa_proporcao_vc: state.app.fixavc,
      exigencias_selecionadas: exigenciasSelecionadas,
      exigencias_alteradas: exigenciasAlteradas,
      user_id: state.app.user.id,
      status: res.status ? res.status : 2,
      erro: res.erro_min ? res.erro_min : null,
      resultado_simplex: res,
    };
    if (cliente && cliente !== null) {
      dieta["id_cliente"] = cliente.id;
    }
    dietas.push(dieta);

    localStorage.setItem("dietas__", JSON.stringify(dietas));
    return dieta;
  };
  const predicao = saveDieta(state, name);
  return Vue.prototype.axios
    .post("dieta/saveFD", { dieta: JSON.stringify(predicao) })
    .then((res) => {
      let md5hash = createMD5(String(res.data.id));
      state.app.dieta_salva = {
        assinatura: md5hash,
        dieta: JSON.parse(res.data.dieta),
        id: res.data.id,
      };
      return res;
    });
};
export const removeDieta = ({ commit, state }, dieta) => {
  return Vue.prototype.axios.post("dieta/remove", { id: dieta.id });
};
export const loadDietaInDietaRefinada = ({ commit, state }, { dieta = undefined, tipo = null, dietaRefinada = null }) => {
  if (dietaRefinada) {
    tipo = _.get(dietaRefinada, 'tipo', 'MN');
    dieta = _.get(dietaRefinada, 'dieta', {});
  }
  if (!_.isUndefined(dieta)) commit('dietaRefinada|setDieta', dieta);
  if (tipo) commit('dietaRefinada|setTipo', tipo);
  return state.app.dietaRefinada;
}
